import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react'
import { fetchAuthSession } from 'aws-amplify/auth'
import {
    Text,
    Image,
    View,
    FlatList,
    TouchableOpacity,
    Keyboard,
    Modal,
    PixelRatio,
    ActivityIndicator,
    Alert,
    Platform,
    Dimensions,
    Pressable,
} from 'react-native'

import {
    FormContentsModel,
    FormsModel,
    ModelAnswerFromProps,
    PerfectStoreTagsModel,
    StepsTypeModel,
    StoreAdditionalInfo,
} from './models/StoreDetailModel'
import { StoreFormItem } from './StoreFormItem'
import { StoreFormMV } from './StoreFormItem'
import { SiNoNaItem, SiNoNaSheet } from './FormItems/SiNoNaItem'
import { InputCommentItem } from './FormItems/InputCommentItem'
import { InputNumberItem } from './FormItems/InputNumberItem'
import { ScrollView } from 'react-native'
import styles from './ExecutionViewStyles'
import theme from '../../../theme'
import { Feather, Ionicons, MaterialCommunityIcons } from '@expo/vector-icons'
import { DropdownItem, DropdownMultipleSelectItem } from './FormItems/DropdownItem'
import { ImageFormItem } from './FormItems/ImageFormItem'
import { InputPhotoItem } from './FormItems/InputPhotoItem'
import { InputPhotoMultipleItem } from './FormItems/InputPhotoMultipleItem'
import { InputPriceNa, InputPriceSheet } from './FormItems/InputPriceNa'
import { StackScreenProps } from '@react-navigation/stack'
import { DotsNavigation } from '../navigation/stacks/NavigationStack'
import { useNavigation } from '@react-navigation/native'
import { schedulesServices } from '../../../database/schedules.database'
import {
    CHECKIN_STATUS,
    FORM_SCHEDULE_STATUS,
    FORM_TYPES,
    STATUS_FORM_PENDING_SYNC,
    STORE_STATUS,
} from './constants/constants.stores'
import { useFormContext, useFormRefrigeratorContext } from './context/form.context'
import { useStoreContext } from './context/store.context'
import {
    getOrCreateFormDayUniqueId,
    clearFormDayUniqueId,
} from '../../utils/formDayUniqueIdStore' // ✅ Importar store
import { generateDayUniqueId } from '../../utils/basicUtils' // ✅ Para generar ID único en creación de heladera
import { getLanguage } from '../appBar/context/LocalizationContext'
import CameraView from '../customUtils/CameraView'
import { Loader } from '../customUtils/Components/Loader'
import { FormHorizontalTags } from './FormHorizontalTags'
import { InputButtonItem } from './FormItems/InputButtonItem'
import { InputFormItem } from './FormItems/InputFormItem'
import { InputCommentNoEditItem } from './FormItems/InputCommentNoEditItem'
import { useFormView } from './hooks/useFormView'
import { InputPhotoImageItem } from './FormItems/InputPhotoImageItem'
import { CampaignStatus } from './StatusTagsStores'
import { useModerationPeriodStore } from '../../redux/zustand/modarationPeriodStore'
import { useStoreStatusStore } from '../../redux/zustand/storesStatusStore'
import {
    getDateNow,
    isValidDateString,
    openExternalLink,
    validateConnectionNetwork,
    retryDbOperation,
} from '../../utils/functions.utils'
import { BottomSheetAlert } from '../modals/BottomSheetAlert'
import {
    request_save_form,
    request_get_store,
} from '../../services/schedule.services'
import {
    saveFormAnswers,
    updateFormAnswerSchedule,
    getFormsAnswersInStore,
    hasRefrigeratorsInCurrentCheckin,
} from '../../../db-sqlite/repositories/schedule.repository'
import { removeCacheByFragment } from '../../utils/FetchServices'
import { getNextAvailableFormIndex } from './helpers/formNavigationHelper'
import { SafeKeyboardAvoidingView } from '../../../SafeKeyboardProvider'
import { ModuleShareItem } from './FormItems/ModuleShareItem'
import { CLIENTS_SLUGS, SKIP_NEXT_FORM_ALERT } from '../../constants/slugs'
import Constants from 'expo-constants'
import { useBottomSheet } from '../customUtils/context/BottomSheetContext'

interface Props extends StackScreenProps<DotsNavigation, 'ExecutionView'> {}

const QUESTION_ID_CLOSE_MOTIVE = '0'
const OPTION_CLOSE = '1'
const OPTION_CLOSE_PERMANENTLY = '2'
const OPTION_CLOSE_OTHER = '3'

const FORM_ALIAS_STATUS = {
    SUCCESS: 1,
    DUPLICATED: -3,
    ERROR: -1,
}

const MESSAGES_FORM_ALIAS = {
    ERROR: 'Ocurrió un error al guardar tu elección. Por favor, intentá nuevamente.',
}

export const ExecutionView = React.memo(({ route }: Props) => {
    const {
        forms,
        selectedIndex,
        schedule_id,
        store_id,
        storeInfo,
        checkin_id,
        status_store,
        statusCheckin,
        // ✅ Parámetros adicionales para formularios hijos de heladera
        refrigerator_id,
        form_id_father: form_id_father_param,
        heladera: heladera_param,
        isCreateHeladera,
        question_id: question_id_param,
    } = route.params
    const { languageFile } = getLanguage()
    const { formsContext, updateStatusForm, setReloadChange } = useFormContext()
    const { reloadForms: storeReloadForms } = useStoreContext()

    const [selectedForm, setForm] = useState<FormsModel>()
    const [formType, setFormType] = useState(0)
    const [formContent, setContents] = useState<FormContentsModel[]>([])
    const [reloadHideShowData, setReloadHideShowData] = useState(false)
    const [reloadForms, setReloadForms] = useState(false)
    const [iconSize, setIconSize] = useState(18)
    const [isFormReady, setIsFormReady] = useState(false)

    const fontScale = 1
    const allFilter = { _id: '-1', name: 'TODOS', isSelected: true }
    const allTags = [allFilter, ...(selectedForm?.tags ?? [])]
    const navigation = useNavigation()
    const { sendForm } = useFormView({
        form_id: selectedForm?._id,
        schedule_id,
        store_id,
        checkin_id,
    })
    const { openBottomSheet, closeBottomSheet } = useBottomSheet()

    // 🔍 DEBUG: Logging para rastrear ciclo de vida del componente
    useEffect(() => {
        console.log('🟢 [COMPONENT MOUNT] ExecutionView montado', {
            schedule_id,
            form_id: selectedForm?._id,
            store_id,
            timestamp: new Date().toISOString(),
        })
        return () => {
            console.log('🔴 [COMPONENT UNMOUNT] ExecutionView desmontado', {
                schedule_id,
                form_id: selectedForm?._id,
                store_id,
                timestamp: new Date().toISOString(),
            })
        }
    }, [])

    useEffect(() => {
        onClickForm(selectedIndex)
    }, [])

    // Si cambia el contexto de formularios (p. ej. se creó una heladera nueva)
    // o si el contexto de stores dispara `reloadForms`, actualizamos el formulario
    useEffect(() => {
        try {
            if (!selectedForm) return

            const isChildForm = !!form_id_father_param

            // Para formularios hijo: no sobrescribir contents ya cargados
            if (isChildForm && formContent && formContent.length > 0) {
                console.log(
                    '⏭️ [ExecutionView] Formulario HIJO ya cargado, skipping context update',
                )
                return
            }

            // Para formularios padre con contents: verificar si hay contenidos nuevos
            if (!isChildForm && formContent && formContent.length > 0) {
                const updated = formsContext.find((f) => f._id === selectedForm._id)
                if (updated) {
                    const contextContentIds = (updated.contents || []).map(
                        (c) => c._id,
                    )
                    const localContentIds = formContent.map((c) => c._id)
                    const newIds = contextContentIds.filter(
                        (id) => !localContentIds.includes(id),
                    )

                    if (newIds.length > 0) {
                        const newContents = (updated.contents || []).map(
                            (content) => ({
                                ...content,
                                hideQuestion: content.status === 3,
                            }),
                        )
                        setForm(updated)
                        setContents(newContents)
                        setIsFormReady(newContents.length > 0)
                    }
                }
                return
            }

            // ✅ Si el formulario viene de route.params (forms) y NO está en formsContext,
            // significa que es un formulario hijo navegado - NO actualizar desde context
            if (forms && forms.length > 0) {
                const formInContext = formsContext.find(
                    (f) => f._id === selectedForm._id,
                )
                if (!formInContext) {
                    return
                }
            }

            // Primero intentamos encontrar la versión actualizada del formulario por _id
            const updated = formsContext.find((f) => f._id === selectedForm._id)
            if (updated) {
                const newContents = (updated.contents || []).map((content) => ({
                    ...content,
                    hideQuestion: content.status === 3,
                }))
                setForm(updated)
                setContents(newContents)
                setIsFormReady(newContents.length > 0)
                return
            }

            // Si no existe (por ejemplo, se creó una heladera nueva que antes no estaba en `forms`),
            // buscamos por `form_id_father` + `question_id` si se pasó esa info
            if (isCreateHeladera && form_id_father_param && question_id_param) {
                const created = formsContext.find(
                    (f) =>
                        f.form_id_father === form_id_father_param &&
                        f.question_id === question_id_param,
                )
                if (created) {
                    const newContents = (created.contents || []).map((content) => ({
                        ...content,
                        hideQuestion: content.status === 3,
                    }))
                    setForm(created)
                    setContents(newContents)
                    setIsFormReady(newContents.length > 0)
                }
            }
        } catch (error) {
            console.error('Error updating form from context:', error)
        }
    }, [formsContext, storeReloadForms])

    useEffect(() => {
        if (formContent.length > 0) {
            setIsFormReady(true)
        }
    }, [formContent])

    // 🔍 Diagnóstico: al recuperar foco, loguear estado de contents local vs contexto
    useEffect(() => {
        const unsubscribe = navigation.addListener('focus', () => {
            if (!selectedForm) return
        })
        return unsubscribe
    }, [navigation, selectedForm, formsContext, formContent, form_id_father_param])

    const onClickForm = useCallback(
        (index: number) => {
            // ✅ FIX: Validar index antes de continuar
            if (index === -1 || !forms || forms.length === 0) {
                console.log('⚠️ [onClickForm] Index inválido o forms vacío')
                return
            }

            if (index >= forms.length) {
                console.log('⚠️ [onClickForm] Index fuera de rango:', {
                    index,
                    forms_length: forms.length,
                })
                return
            }

            // ✅ FIX: Usar forms (route.params) para verificar status, NO formsContext
            try {
                const formToLoad = forms[index]
                const repeat = formToLoad.repeat
                const status = formToLoad.status
                const isFormRedirect =
                    formToLoad.type_form?.type === FORM_TYPES.FORM_REDIRECT

                if (
                    (status === FORM_SCHEDULE_STATUS.COMPLETED ||
                        status === FORM_SCHEDULE_STATUS.PENDING_CONNECTION) &&
                    !repeat &&
                    !isFormRedirect
                ) {
                    return () => {}
                }
            } catch (error) {
                console.error('71 ---- error: ', error)
            }

            if (selectedForm != null && selectedForm._id === forms[index]._id) {
                return
            }

            const type = forms[index]?.type_form?.type ?? 0

            if (type == FORM_TYPES.FORM_REDIRECT) {
                const url = forms[index]?.link
                const formRedirect = forms[index]
                const isValidUrl =
                    url && /^https?:\/\/[\w.-]+\.[a-z]{2,}.*$/.test(url)
                if (!isValidUrl) {
                    console.log('URL inválida o no disponible:', url)
                    return
                }

                console.log('Navegando a WebViewScreen', url)
                const params = {
                    link: url,
                }
                updateStatusForm(
                    formsContext,
                    schedule_id,
                    formRedirect._id,
                    store_id,
                    FORM_SCHEDULE_STATUS.PENDING_CONNECTION,
                )
                setReloadChange()
                const sendForm = async () => {
                    const registerUpdated =
                        await schedulesServices.updateStatusFormAnswerSchedule(
                            FORM_SCHEDULE_STATUS.PENDING_CONNECTION,
                            schedule_id,
                            formRedirect._id,
                            store_id,
                            'sin conexion',
                            null,
                            checkin_id,
                        )

                    await schedulesServices.updateStatusSendForm(
                        FORM_SCHEDULE_STATUS.PENDING_CONNECTION,
                        schedule_id,
                        formRedirect._id,
                        store_id,
                        'sin conexion',
                        registerUpdated.updatedId,
                    )
                }
                sendForm()
                // navigation.navigate('WebView', params as undefined)
                openExternalLink(url)
                return
            }

            let newContents = forms[index].contents.map((content) => ({
                ...content,
                hideQuestion: content.status === 3,
            }))

            console.log('✅ [onClickForm] Seteando contents:', {
                form_id: forms[index]._id,
                contents_count: newContents?.length,
                formType: type,
            })

            setFormType(type)
            setContents(newContents ?? [])
            // console.log('=====> selected form: ', forms[index])
            setForm(forms[index])
            scrollToCurrentForm(index)
        },
        [formsContext, forms, selectedForm],
    )

    const filterByTag = (tag: string, data?: FormContentsModel[]) => {
        console.log('🚀 ~ filterByTag ~ data:', data?.length)
        // console.log('🚀 ~ filterByTag ~ tag:', tag)
        let dataMap = data && data.length > 0 ? data : formContent
        let updatedFormContent = []
        if (tag != '-1') {
            updatedFormContent = dataMap.map((question) => {
                if (question.tag && question.tag._id != tag) {
                    return {
                        ...question,
                        hideByFilter: true,
                    }
                }
                return {
                    ...question,
                    hideByFilter: false,
                }
            })
        } else {
            updatedFormContent = dataMap.map((question) => {
                return {
                    ...question,
                    hideByFilter: false,
                }
            })
        }

        setContents(updatedFormContent)

        setReloadHideShowData(!reloadHideShowData)
    }
    const reloadFormsData = () => {
        setReloadForms(!reloadForms)
    }
    const getStatusForm = () => {
        if (selectedForm) {
            const formIndex = formsContext.findIndex(
                (form) => form._id === selectedForm._id,
            )
            // console.log("🚀 ~ getStatusForm ~ formIndex:", formIndex)

            const form = formsContext[formIndex]
            // console.log("🚀 ~ getStatusForm ~ form:", form)

            if (form) {
                return form.status
            }
        }
        return null
    }

    const expoConfig = Constants.expoConfig || { slug: '' }

    const handleFormSubmitted = useCallback(() => {
        if (!store_id) {
            navigation.goBack()
            return
        }

        // Construir array con status actualizado usando formsContext (dinámico)
        // Forzar el form recién enviado como PENDING_CONNECTION para evitar race conditions
        const formsWithUpdatedStatus = forms.map((form) => {
            if (form._id === selectedForm?._id) {
                return { ...form, status: FORM_SCHEDULE_STATUS.PENDING_CONNECTION }
            }
            const contextForm = formsContext.find((fc) => fc._id === form._id)
            if (contextForm) {
                return {
                    ...form,
                    status: contextForm.status ?? form.status,
                    repeat: contextForm.repeat ?? form.repeat,
                }
            }
            return form
        })

        const nextIndex = getNextAvailableFormIndex(
            formsWithUpdatedStatus,
            selectedForm?._id,
        )

        const showNextFormAlert = !SKIP_NEXT_FORM_ALERT.includes(expoConfig?.slug)

        if (nextIndex !== -1 && showNextFormAlert) {
            closeBottomSheet(true) // cierro modal de confirmación antes de abrir el de navegación
            let title = selectedForm.name_form + ' guardado correctamente'
            let desc =
                ' hasta que todos los formularios que hayas terminado aparezcan completos y marcados en verde en la tienda.'
            let button = 'Siguiente'
            openBottomSheet(
                <BottomSheetAlert
                    image={require('../../../assets/formCheckSend.png')}
                    title={title}
                    desc={desc}
                    buttonGoto={button}
                    goto={() => {
                        onClickForm(nextIndex)
                        closeBottomSheet(true)
                    }}
                    button={'Volver a tienda'}
                    closeBottomSheet={() => {
                        navigation.goBack()
                        closeBottomSheet(true)
                    }}
                    closeButtonStyles={{ borderWidth: 0 }}
                    boldDesc={'No borres la caché'}
                />,
                null,
                false,
                false,
                false,
            )
        } else {
            navigation.goBack()
        }
    }, [store_id, forms, formsContext, selectedForm, navigation, onClickForm])

    const RenderNoFormSelected = () => {
        return (
            <View style={styles.containerNoMessage}>
                <Image
                    style={styles.imageNoMessage}
                    source={require('../../../assets/noFormSelected.png')}
                />
                <Text style={styles.titleNoMessage}>{languageFile.select_form}</Text>
                <Text style={styles.descriptionNoMessage}>
                    {languageFile.select_one_form}
                </Text>
            </View>
        )
    }

    const renderForm = useMemo(() => {
        if (
            formContent == undefined ||
            formContent == null ||
            formContent.length == 0
        ) {
            return <RenderNoFormSelected />
        }

        let formData = formContent.sort((a, b) => a.index - b.index)

        switch (formType) {
            case FORM_TYPES.FORM_REFRIGERATOR:
            case FORM_TYPES.FORM_POP_HELADERA_CHILD:
            case FORM_TYPES.FORM_MODERACION:
            case FORM_TYPES.FORM_POP_HELADERA:
            case FORM_TYPES.FORM_IMAGE:
            case FORM_TYPES.FORMS:
                // return formContent.length > 0 && <FormView formContent={formContent} hideOrShow={hideOrShow} form_id={selectedForm?._id} />
                return (
                    <MemoizedFormView
                        key={selectedForm?._id}
                        formContent={formData}
                        form_id={selectedForm?._id}
                        schedule_id={schedule_id}
                        store_id={store_id}
                        repeat={selectedForm.repeat}
                        progress={selectedForm.progress}
                        totalScore={selectedForm.totalScore}
                        show_barcode={selectedForm.show_barcode || false}
                        hasFinishSheet={false}
                        reloadHideShowData={reloadHideShowData}
                        checkin_id={checkin_id}
                        tags={selectedForm?.tags?.length > 0 ? allTags : []}
                        // onClickTag={filterByTag}
                        name_form={selectedForm.name_form}
                        reloadFormsData={reloadFormsData}
                        formType={formType}
                        status_store={status_store}
                        statusCheckin={statusCheckin}
                        getStatusForm={getStatusForm}
                        formTypeObj={selectedForm.type_form}
                        refrigerator_id={refrigerator_id}
                        form_id_father_param={form_id_father_param}
                        heladera_param={heladera_param}
                        isCreateHeladera={isCreateHeladera}
                        question_id_param={question_id_param}
                        onFormSubmitted={store_id ? handleFormSubmitted : undefined}
                        formHeader={{
                            subTitle: selectedForm?.sub_title ?? '',
                            description: selectedForm?.description ?? '',
                        }}
                    />
                )

            default:
                return <RenderNoFormSelected />
        }
    }, [
        formType,
        formContent,
        selectedForm,
        refrigerator_id,
        form_id_father_param,
        heladera_param,
        handleFormSubmitted,
    ])
    const tagsRef = useRef(null)

    const scrollToCurrentForm = (index: number) => {
        if (!tagsRef.current) return
        tagsRef.current?.scrollToIndex({
            index: index,
            animated: true,
        })
    }
    return (
        <SafeKeyboardAvoidingView style={styles.container}>
            {forms.length > 1 ? (
                <View>
                    {storeInfo && (
                        <>
                            <View style={styles.containerGeneral}>
                                <View style={styles.containerNameStore}>
                                    <MaterialCommunityIcons
                                        name='store'
                                        size={iconSize}
                                        color={'black'}
                                    />

                                    <Text
                                        style={[
                                            styles.textNameStore,
                                            {
                                                fontWeight: '600',
                                                flexShrink: 1,
                                                marginStart: 5,
                                            },
                                        ]}
                                    >
                                        {storeInfo.code && (
                                            <Text
                                                style={[
                                                    styles.textCodeStore,
                                                    {
                                                        color: theme.colors
                                                            .textPrimary,
                                                        fontWeight: '600',
                                                        fontSize: 16,
                                                    },
                                                ]}
                                            >
                                                {storeInfo.code}
                                                {' | '}
                                            </Text>
                                        )}
                                        {storeInfo.name}
                                    </Text>
                                </View>

                                {storeInfo.address && storeInfo.address != '-' && (
                                    <Text style={[styles.textAddressStore]}>
                                        {storeInfo.address}

                                        {storeInfo.cluster && (
                                            <Text style={{}}>
                                                {' | '}
                                                {storeInfo.cluster}
                                            </Text>
                                        )}
                                        {storeInfo.distance && (
                                            <>
                                                <Text style={styles.textInfoStore}>
                                                    {' | '}
                                                </Text>

                                                <Feather
                                                    name='map-pin'
                                                    size={18}
                                                    color={
                                                        theme.colors.textSecondary
                                                    }
                                                />

                                                <Text style={styles.textInfoStore}>
                                                    {' '}
                                                    {storeInfo.distance}
                                                </Text>
                                            </>
                                        )}
                                    </Text>
                                )}
                                <View
                                    style={[
                                        styles.flexDirectionRow,
                                        styles.marginHorizontal,
                                    ]}
                                >
                                    {storeInfo.additional && (
                                        <AdditionalView
                                            additional={storeInfo.additional}
                                        />
                                    )}
                                </View>
                            </View>
                        </>
                    )}

                    <FlatList
                        ref={tagsRef}
                        data={forms}
                        contentContainerStyle={{
                            flexDirection: 'row',
                            marginBottom: 8,
                        }}
                        renderItem={({ item: form, index }) => {
                            const isSelected = selectedForm === form
                            const image = isSelected ? form?.icon_check : form?.icon

                            return (
                                <StoreFormItem
                                    key={form?._id + index}
                                    name={form?.name_form}
                                    image={image}
                                    index={index}
                                    onClickItem={onClickForm}
                                    isSelected={isSelected}
                                />
                            )
                        }}
                        keyExtractor={(item, index) => item._id.toString()}
                        horizontal
                        showsHorizontalScrollIndicator={false}
                        onScrollToIndexFailed={(info) => {
                            const wait = new Promise((resolve) =>
                                setTimeout(resolve, 500),
                            )
                            wait.then(() => {
                                tagsRef.current?.scrollToIndex({
                                    index: info.index,
                                    animated: true,
                                })
                            })
                        }}
                    />
                </View>
            ) : (
                <View style={styles.containerFilters}>
                    {forms.map((form, index) => {
                        return (
                            <StoreFormMV
                                key={form?._id + index}
                                name={form?.name_form}
                            />
                        )
                    })}
                </View>
            )}

            {isFormReady ? (
                renderForm
            ) : (
                <ActivityIndicator size='large' color={theme.colors.colorPrimary} />
            )}
        </SafeKeyboardAvoidingView>
    )
})

export const AdditionalView = ({
    additional,
}: {
    additional: StoreAdditionalInfo[]
}) => {
    return (
        <View style={styles.flexDirectionRow}>
            {additional.map((item, index) => {
                if (item.label) {
                    switch (item.label) {
                        case 'segment':
                        case 'route':
                            return (
                                <Text key={item.id} style={styles.textCodeStore}>
                                    , {item.value}
                                </Text>
                            )
                        default:
                            return null
                    }
                }
            })}
        </View>
    )
}

interface FormViewProps {
    formContent: FormContentsModel[]
    hideOrShow?: (
        hide?: string[],
        show?: string[],
        data?: FormContentsModel[],
    ) => void
    store_id?: string
    form_id?: string
    schedule_id?: string
    repeat?: number
    progress?: boolean
    totalScore?: number
    clearRegisterDatabaseLocal?: (type?: string) => void
    show_barcode?: boolean
    hasFinishSheet?: boolean
    reloadHideShowData?: boolean
    checkin_id?: string
    tags: PerfectStoreTagsModel[]
    onClickTag?: (id: string, data?: FormContentsModel[]) => void
    name_form?: string
    formType?: number
    reloadFormsData?: () => void
    actionButonSend?: (statusIncidence?: number, incidenteResponse?: string) => void
    title?: string
    status_store?: number
    statusCheckin?: number | undefined
    getStatusForm?: () => number | null
    formTypeObj: StepsTypeModel
    deleteForm?: ((a) => any) | undefined
    // ✅ Parámetros adicionales para formularios hijos de heladera
    refrigerator_id?: string
    form_id_father_param?: string
    heladera_param?: boolean
    isCreateHeladera?: boolean
    question_id_param?: string
    onFormSubmitted?: () => void
    formHeader?: { subTitle?: string; description?: string }
}

export const FormView = ({
    formContent,
    hideOrShow,
    store_id,
    form_id,
    schedule_id,
    repeat,
    progress,
    totalScore,
    clearRegisterDatabaseLocal,
    show_barcode,
    hasFinishSheet,
    reloadHideShowData,
    checkin_id,
    tags,
    onClickTag,
    name_form,
    formType,
    reloadFormsData,
    actionButonSend = undefined,
    title,
    status_store,
    statusCheckin,
    getStatusForm,
    formTypeObj,
    deleteForm,
    // ✅ Parámetros adicionales para formularios hijos de heladera
    refrigerator_id,
    form_id_father_param,
    heladera_param,
    isCreateHeladera,
    question_id_param,
    onFormSubmitted,
    formHeader,
}: FormViewProps) => {
    // console.log('==========================> name_form en FormView:', name_form)
    const {
        isOpen,
        sendForm,
        sendFormsPending,
        reloadData,
        isLoadingReload,
        openBottomSheet,
        closeBottomSheet,
        setIsLoadingReload,
        setbuttonActive,
        buttonActive,
    } = useFormView({
        checkin_id,
        clearRegisterDatabaseLocal,
        form_id,
        hasFinishSheet,
        reloadFormsData,
        schedule_id,
        store_id,
        title,
    })

    const {
        formsContext,
        setFormsContext,
        setReloadChange,
        updateProgressForm,
        updateStatusForm,
        currentCheckin,
        updateStatusProgressForm,
    } = useFormContext()
    const {
        getFormRefrigerator,
        updateActveFormRefrigerator,
        form_refrigerators_main,
        forms_refrigerators,
    } = useFormRefrigeratorContext()

    const { setReloadFormsFn } = useStoreContext()
    const { languageFile } = getLanguage()
    const navigation = useNavigation()

    const isFormIncidence = formType == FORM_TYPES.FORM_INCIDENCE
    const isFormAlias = formType == FORM_TYPES.FORM_ALIAS
    // isCreateHeladera viene del prop (true solo cuando se crea heladera nueva desde Type 16)
    const isFormCreateRefrigerator =
        formType == FORM_TYPES.FORM_POP_HELADERA ||
        formType == FORM_TYPES.FORM_REFRIGERATOR ||
        isCreateHeladera === true

    const [data, setData] = useState([])
    // filteredData se recalcula sincrónicamente cuando data cambia (sin render extra)
    const filteredData = useMemo(
        () => data.filter((item) => !item.hideQuestion && !item.hideByFilter),
        [data],
    )
    const [answers, setAnswers] = useState([])
    const [isSending, setIsSending] = useState(false)
    const [incompleteQuestionIds, setIncompleteQuestionIds] = useState<Set<string>>(
        new Set(),
    )
    const [keyboardHidden, setKeyboardHidden] = useState(true)
    const [keyboardHeight, setKeyboardHeight] = useState(0)
    const keyboardHeightRef = useRef(0)
    const [keyboardScrollPadding, setKeyboardScrollPadding] = useState(0)
    const pendingScrollIndexRef = useRef<number | null>(null) // índice a scrollear cuando el teclado abra
    const pendingScrollViewPositionRef = useRef<number | null>(null) // viewPosition override para el scroll pendiente (null = calcular dinámicamente)
    const lastFocusedScrollIndexRef = useRef<number | null>(null) // último índice enfocado (persiste aunque se cierre el teclado)
    const isProgrammaticScrollRef = useRef(false) // true mientras scrollToIndex está animando
    const currentScrollOffsetRef = useRef(0) // offset actual del FlatList (actualizado en onScroll)
    const pendingSubInputScreenYRef = useRef<{ y: number; h: number } | null>(null) // posición pantalla del sub-input pendiente (ModuleShareItem)
    const flatListHeightRef = useRef(0) // alto del FlatList con teclado abierto (o sin él si no cambió)
    const flatListHeightNoKBRef = useRef(0) // alto del FlatList SIN teclado (para detectar modo)
    const [activeInputId, setActiveInputId] = useState(null)
    const activeInputIdRef = useRef(null)
    const [isFirstLoad, setIsFirstLoad] = useState(true)
    const [messageFormByResponseRequest, setMessageFormByResponseRequest] =
        useState(null)
    const isSendingForm = useRef(false)
    const [isFormRegistered, setIsFormRegistered] = useState(false) // ✅ FIX: Rastrear si el registro está creado

    // ✅ FIX: Usar refs para evitar loops infinitos
    const lastFormContentRef = useRef<string>('')
    const lastAnswersRef = useRef<string>('')
    // Ref para llevar la cuenta de respuestas ya persistidas en BD
    const lastPersistedAnswersCountRef = useRef<number>(0)

    // ✅ FIX #4: Usar store centralizado para day_unique_id
    // Esto asegura que el mismo day_unique_id se use en CREATE y UPDATE
    // ✅ FIX WISE: Para WISE, NO usar el store (cada instancia debe ser independiente)
    const formDayUniqueId =
        schedule_id === 'wise'
            ? undefined // WISE no debe usar day_unique_id del store
            : getOrCreateFormDayUniqueId(form_id, schedule_id, store_id)

    const getStoreStatus = useStoreStatusStore((state) => state.getStoreStatus)
    const isUserBlocked = useModerationPeriodStore((state) => state.isUserBlocked)

    const initializeForm = async () => {
        setData(formContent)

        // ✅ FIX: Esperar a que el registro esté creado antes de checkCompleteInit
        // Esto evita race condition entre createFormQuestionAnswer y checkCompleteInit
        if (!isFormRegistered) {
            console.log('⏳ initializeForm esperando a que el registro se cree...')
            return // Salir, checkCompleteInit se ejecutará después cuando isFormRegistered = true
        }

        console.log(
            '✅ initializeForm - Registro confirmado, ejecutando checkCompleteInit',
        )
        await checkCompleteInit()
    }

    const createFormQuestionAnswer = useCallback(async () => {
        if (!formContent || formContent.length == 0) return
        try {
            // console.log('📝 createFormQuestionAnswer - Iniciando:', {
            //     form_id,
            //     schedule_id,
            //     store_id,
            //     day_unique_id: formDayUniqueId,
            //     formType,
            //     name_form,
            //     isFormCreateRefrigerator,
            // })
            // console.log(
            //     '📝 [ANTES DE TODO] Estado answers al inicio de createFormQuestionAnswer:',
            //     JSON.stringify(answers),
            //     new Date().toISOString(),
            // )

            let name_form_value = name_form
            if (!name_form_value || name_form_value === undefined) {
                try {
                    name_form_value = await schedulesServices.getFormName(form_id)
                } catch (error) {
                    console.error('❌ Error obteniendo name_form:', error)
                }
            }

            // ✅ Verificar si ya hay heladeras creadas/auditadas en el checkin actual
            // Si es formulario de heladeras Y ya hay heladeras en el checkin, status = IN_PROGRESS
            let status_forms = FORM_SCHEDULE_STATUS.PENDING

            if (isFormCreateRefrigerator && checkin_id && store_id) {
                const hasRefrigerators = await hasRefrigeratorsInCurrentCheckin(
                    checkin_id,
                    store_id,
                )
                if (hasRefrigerators) {
                    status_forms = FORM_SCHEDULE_STATUS.IN_PROGRESS
                    console.log(
                        '✅ Heladeras detectadas en checkin actual - Status establecido a IN_PROGRESS',
                    )
                } else {
                    console.log(
                        'ℹ️ No hay heladeras previas en checkin - Status establecido a PENDING',
                    )
                }
            }

            // ✅ SOLUCIÓN SIMPLE: Usar valores de route.params si existen (formulario hijo de heladera)
            console.log('📝 Valores recibidos en route.params:', {
                refrigerator_id,
                form_id_father_param,
                heladera_param,
            })

            // ✅ FIX HELADERA: Si es creación de heladera, eliminar registro anterior del día
            // para que siempre se cree un registro nuevo vacío
            if (isCreateHeladera === true) {
                console.log(
                    '🗑️ isCreateHeladera: Eliminando registro anterior del día antes de crear nuevo...',
                )
                try {
                    await schedulesServices.deleteFormAnswer(
                        schedule_id,
                        form_id,
                        store_id,
                        formDayUniqueId,
                    )
                    console.log('✅ Registro anterior eliminado (si existía)')
                } catch (deleteError) {
                    // Si no existe registro, no es error
                    console.log(
                        'ℹ️ No había registro anterior para eliminar:',
                        deleteError,
                    )
                }
            }

            // Determinar valores según parámetros de ruta o defaults
            const heladera_value =
                heladera_param !== undefined ? heladera_param : null
            // ✅ FIX: Validar que form_id_father no sea '0' ni string vacío
            const form_id_father_value =
                form_id_father_param &&
                form_id_father_param !== '' &&
                form_id_father_param !== '0'
                    ? form_id_father_param
                    : null
            const create_refrigerator_value = isCreateHeladera === true ? 1 : null

            console.log('✅ Usando valores para form_save:', {
                heladera: heladera_value,
                form_id_father: form_id_father_value,
                create_refrigerator: create_refrigerator_value,
            })

            const form_save = {
                schedule_id,
                form_id,
                store_id,
                repeat,
                questions: JSON.stringify(
                    formContent.map((question) => {
                        return {
                            _id: question._id,
                            title: question.title,
                            ponderar:
                                question.questionScore && question.questionScore > 0,
                            questionScore: question.questionScore,
                            input_type: question.input_type,
                            secondary_actions: question.secondary_actions,
                            mandatory: question.mandatory,
                            score: question.score,
                        }
                    }) ?? '[]',
                ),
                progress,
                totalScore,
                status: status_forms,
                name_form: name_form_value,
                heladera: heladera_value,
                create_refrigerator: create_refrigerator_value,
                type_form: formTypeObj,
                form_id_father: form_id_father_value,
                day_unique_id: formDayUniqueId,
                // ✅ Agregar respuestas pre-cargadas (input_notedit type 38) al crear el registro
                answers: JSON.stringify(
                    formContent
                        .filter((q) => q && q.input_type && q.input_type.type === 38)
                        .map((item) => {
                            const answerScore =
                                item.questionScore != null && item.questionScore > 0
                                    ? item.questionScore.toString()
                                    : '0'
                            return {
                                _id: item._id,
                                option_selected_text: [],
                                option_selected_id: [],
                                question_id: item._id,
                                media: '',
                                question_text: item.title,
                                score: answerScore,
                                status: 1,
                                date_finish: new Date().toISOString(),
                                date_init: new Date().toISOString(),
                                question_input: item.questionInput || '',
                                field_media: '',
                                field_text: '',
                                questionScore: item.questionScore,
                                type: item.input_type.type,
                                ponderar: item.score && item.score > 0,
                                question: item.title,
                                questionOptionSelected: [],
                                questionOptionSelectedText: [],
                                questionType: {
                                    title: item.input_type.title,
                                    type: item.input_type.type,
                                    id: item.input_type._id,
                                },
                                questionPrice: '',
                                description: '',
                                tag: '',
                                questionImage: '',
                                questionInput: item.questionInput || '',
                            }
                        }) || [],
                ),
            }

            console.log('🔍 DEBUG createFormQuestionAnswer - form_save final:', {
                heladera: form_save.heladera,
                form_id_father: form_save.form_id_father,
                create_refrigerator: form_save.create_refrigerator,
            })

            const saveResult = await saveFormAnswers(form_save)
            console.log(
                '✅ createFormQuestionAnswer - Registro creado/actualizado exitosamente',
            )
            const savedRecord = saveResult?.result
            const savedExistingAnswers = savedRecord?.answers
                ? JSON.parse(savedRecord.answers)
                : []

            const form = {
                activity_id: form_id,
                status: status_forms,
                date: new Date().toISOString(),
                form_name: name_form_value || undefined, // ✅ Usar name_form_value obtenido de BD si era undefined
                status_send: STATUS_FORM_PENDING_SYNC.NO_SENDED,
                schedule_id: schedule_id,
                count: 1,
                store_id: store_id,
                checkin_id: checkin_id,
            }

            // ✅ FIX: No agregar a FORMS_PENDING_SYNC si es un formulario "wise"
            // Los formularios wise no tienen schedule_id tradicional y no requieren sincronización de agenda
            // También validar que form_name no sea undefined para evitar errores en addFormPendingSync
            if (schedule_id !== 'wise' && form.form_name !== undefined) {
                console.log('addFormPendingSync => form que le envio =====> ', form)
                await schedulesServices.addFormPendingSync(form)
            } else {
                if (schedule_id === 'wise') {
                    console.log(
                        '⚠️ Formulario WISE detectado - saltando addFormPendingSync',
                    )
                } else {
                    console.log(
                        '⚠️ form_name undefined - saltando addFormPendingSync para evitar error',
                    )
                }
            }

            // ✅ FIX: Marcar que el registro está creado para permitir checkCompleteInit
            console.log(
                '🔄 [setIsFormRegistered] Marcando registro como creado - isFormRegistered = true',
                new Date().toISOString(),
            )
            // console.log(
            //     '🔄 [setIsFormRegistered] Estado answers en este momento:',
            //     JSON.stringify(answers),
            // )
            setIsFormRegistered(true)

            // ✅ OPTIMIZACIÓN: Reducir delay de 200ms a 50ms para acelerar creación de heladera
            // Esperar un breve periodo para dar tiempo a que los componentes hijos
            // monten y llamen a `checkComplete`, así las respuestas pre-set
            // (input_notedit) se agreguen al state `answers` antes de persistir.
            await new Promise((resolve) => setTimeout(resolve, 50))

            // Persistir respuestas que se agregaron en memoria antes de que el registro existiera
            try {
                // ✅ FIX WISE: Para formularios WISE, solo limpiar si el registro NO tenía respuestas previas
                // Si el registro ya existía con respuestas (ej: usuario mató la app y volvió),
                // checkCompleteInit ya las cargó. No pisar con setAnswers([]).
                if (schedule_id === 'wise') {
                    if (savedExistingAnswers.length > 0) {
                        console.log(
                            '📝 WISE: Registro existente con respuestas previas, NO limpiando estado',
                            { savedAnswersCount: savedExistingAnswers.length },
                        )
                        lastPersistedAnswersCountRef.current =
                            savedExistingAnswers.length
                    } else {
                        console.log(
                            '📝 WISE: Registro nuevo o sin respuestas, limpiando estado',
                        )
                        setAnswers([])
                        lastPersistedAnswersCountRef.current = 0
                    }
                } else {
                    // Si no tenemos `answers` en el estado local, pero creamos respuestas
                    // pre-cargadas al insertar, sincronizamos el state local con ellas.
                    const initialAnswers = JSON.parse(form_save.answers || '[]')
                    if (
                        (answers == null ||
                            !Array.isArray(answers) ||
                            answers.length === 0) &&
                        initialAnswers.length > 0
                    ) {
                        console.log(
                            '🔍 [setAnswers #2] BEFORE setAnswers(initialAnswers) - Estado previo:',
                            JSON.stringify(answers),
                        )
                        console.log(
                            '🔍 [setAnswers #2] Nuevo valor:',
                            JSON.stringify(initialAnswers),
                            new Date().toISOString(),
                        )
                        setAnswers(initialAnswers)
                        console.log(
                            '🔍 [setAnswers #2] AFTER setAnswers(initialAnswers)',
                        )
                    }

                    if (answers && Array.isArray(answers) && answers.length > 0) {
                        console.log(
                            'ℹ️ Persistiendo respuestas pendientes en BD:',
                            answers.length,
                        )
                        for (const ans of answers) {
                            try {
                                // remplaceAnswers=false, clearAnswers=false
                                await updateFormAnswerSchedule({
                                    newAnswer: ans,
                                    schedule_id,
                                    form_id,
                                    store_id,
                                    replaceAnswers: false,
                                    clearAnswers: false,
                                    type: 'init_persist',
                                    day_unique_id: formDayUniqueId,
                                })
                            } catch (errAns) {
                                console.error(
                                    'Error persistiendo answer pendinte:',
                                    errAns,
                                )
                            }
                        }
                        // Marcar cuántas respuestas persistimos inicialmente
                        lastPersistedAnswersCountRef.current = Array.isArray(answers)
                            ? answers.length
                            : initialAnswers.length
                    }
                }
            } catch (err) {
                console.error('Error al persistir respuestas pendientes:', err)
            }
        } catch (error) {
            console.error('Error en createFormQuestionAnswer:', error)
        }
    }, [
        formContent,
        formDayUniqueId,
        heladera_param,
        form_id_father_param,
        isFormCreateRefrigerator,
        checkin_id,
        store_id,
    ])

    useEffect(() => {
        createFormQuestionAnswer()
    }, [createFormQuestionAnswer])

    // ✅ FIX: Ejecutar checkCompleteInit cuando el registro esté confirmado
    useEffect(() => {
        if (isFormRegistered && formContent.length > 0) {
            // console.log(
            //     '🎯 [useEffect checkCompleteInit] Registro confirmado, ejecutando checkCompleteInit ahora',
            // )
            // console.log(
            //     '🎯 [useEffect checkCompleteInit] Estado actual answers ANTES de llamar checkCompleteInit:',
            //     JSON.stringify(answers),
            // )
            // console.log(
            //     '🎯 [useEffect checkCompleteInit] Timestamp:',
            //     new Date().toISOString(),
            // )
            checkCompleteInit()
        }
    }, [isFormRegistered])

    // Persistir de forma reactiva nuevas respuestas que lleguen después de que el registro exista
    useEffect(() => {
        // ✅ FIX WISE: Para formularios WISE, NO persistir automáticamente las respuestas reactivas
        // WISE tiene su propio flujo de persistencia directa en cada checkComplete
        if (schedule_id === 'wise') {
            console.log('📝 WISE: Saltando persistencia reactiva automática')
            return
        }

        if (!isFormRegistered) return

        const totalAnswers = Array.isArray(answers) ? answers.length : 0
        const lastPersisted = lastPersistedAnswersCountRef.current || 0

        if (totalAnswers > lastPersisted) {
            const toPersist = answers.slice(lastPersisted)
            ;(async () => {
                console.log(
                    'ℹ️ Persistiendo respuestas reactivas en BD:',
                    toPersist.length,
                )
                for (const ans of toPersist) {
                    try {
                        await updateFormAnswerSchedule({
                            newAnswer: ans,
                            schedule_id,
                            form_id,
                            store_id,
                            replaceAnswers: false,
                            clearAnswers: false,
                            type: 'reactive_persist',
                            day_unique_id: formDayUniqueId,
                        })
                    } catch (err) {
                        console.error('Error persistiendo respuesta reactiva:', err)
                    }
                }
                lastPersistedAnswersCountRef.current = Array.isArray(answers)
                    ? answers.length
                    : 0
            })()
        }
    }, [isFormRegistered, answers, schedule_id, form_id, store_id, formDayUniqueId])

    const checkStatusProgresForm = useCallback(async () => {
        try {
            const { progress_questions_yes_na, status } =
                await getFormsAnswersInStore(
                    schedule_id,
                    form_id,
                    store_id,
                    null,
                    repeat,
                )

            // console.log('🔍 [checkStatusProgresForm] Status obtenido de BD:', {
            //     status,
            //     progress_questions_yes_na,
            //     form_id,
            // })

            // Wise forms no usan formsContext (se manejan con estado local en WiseScreen)
            if (schedule_id !== 'wise') {
                updateStatusProgressForm(
                    formsContext,
                    schedule_id,
                    form_id,
                    store_id,
                    status,
                    progress_questions_yes_na,
                )
            }

            // console.log(
            //     '✅ [checkStatusProgresForm] Contexto actualizado con status:',
            //     status,
            // )

            // await Promise.all([
            //     updateProgressForm(formsContext, schedule_id, form_id, store_id, progress_questions_yes_na),
            //     updateStatusForm(formsContext, schedule_id, form_id, store_id, status)
            // ])
        } catch (error) {
            console.error('Error en checkStatusProgresForm:', error)
        }
    }, [form_id, schedule_id, store_id, formsContext]) // ✅ Agregar formsContext para que tenga la referencia actualizada

    const checkCompleteInit = async (): Promise<void> => {
        try {
            console.log(
                '🎯 checkCompleteInit ejecutándose, obteniendo respuestas de BD',
            )
            const {
                fqa_id,
                answers: answersFromDB,
                totalScore,
                scoreObtained,
                progress_questions_yes_na,
                status,
            } = await getFormsAnswersInStore(
                schedule_id,
                form_id,
                store_id,
                null,
                repeat,
            )
            console.log('🔍 [checkCompleteInit] Status obtenido de BD:', {
                status,
                progress_questions_yes_na,
                fqa_id,
            })
            // console.log(
            //     '🔍 [setAnswers #3] BEFORE setAnswers(answersFromDB) en checkCompleteInit',
            // )
            // console.log(
            //     '🔍 [setAnswers #3] Estado previo answers:',
            //     JSON.stringify(answers),
            // )
            // console.log(
            //     '🔍 [setAnswers #3] Nuevo valor answersFromDB:',
            //     JSON.stringify(answersFromDB),
            //     new Date().toISOString(),
            // )
            setAnswers(answersFromDB)
            // console.log('🔍 [setAnswers #3] AFTER setAnswers(answersFromDB)')
            console.log('🚀 ~ checkCompleteInit ~ answers:', answersFromDB.length)
            console.log('🚀 ~ checkCompleteInit ~ formContent:', formContent.length)

            // ✅ FIX: Usar data (state actual) en lugar de formContent para preservar hideQuestion
            const sourceData = data.length > 0 ? data : formContent
            const newFormContent = updateFormContent(sourceData, answersFromDB)
            updateProgressForm(
                formsContext,
                schedule_id,
                form_id,
                store_id,
                progress_questions_yes_na,
            )
            updateStatusForm(formsContext, schedule_id, form_id, store_id, status)
            //ak calculo porcentaje y reviso boton

            let completo = true
            let totalResponse = answersFromDB.length
            let totalQuestions = 0
            console.log('📋 [INIT] Iniciando validación en checkCompleteInit')
            newFormContent.forEach((item, index) => {
                const isMandatory = item.mandatory !== false
                const validHideQuestion =
                    item.status !== 3 || item.hideQuestion === false

                if ((item.requiered || validHideQuestion) && isMandatory) {
                    let answerResponse = answersFromDB.find(
                        (answer) => answer._id === item._id,
                    )
                    // console.log(
                    //     `🔍 [INIT-Q${index}] "${item.title.substring(0, 40)}" (${item._id}) - Type: ${item.input_type.type}, hideQuestion: ${item.hideQuestion}, Respondida: ${!!answerResponse}`,
                    // )

                    if (answerResponse !== undefined) {
                        if (item.input_type.type === 14) {
                            let secondarySelected = item.secondary_actions.find(
                                (action) =>
                                    action._id ===
                                    answerResponse.option_selected_id[0],
                            )
                            // if (secondarySelected?.show_questions) {
                            //     console.log(`   📌 Debe mostrar ${secondarySelected.show_questions.length} preguntas condicionales:`, secondarySelected.show_questions)
                            // }

                            secondarySelected.show_questions.forEach((question) => {
                                // ✅ Solo validar si la pregunta está visible
                                const questionItem = newFormContent.find(
                                    (q) => q._id === question,
                                )
                                const isVisible =
                                    questionItem &&
                                    questionItem.hideQuestion !== true
                                let contesto = answers.find(
                                    (answer) => answer._id === question,
                                )
                                console.log(
                                    `      ↳ Pregunta ${question}: Visible=${isVisible}, Respondida=${!!contesto}`,
                                )
                                if (isVisible && contesto == undefined) {
                                    completo = false
                                    // console.log(
                                    //     `      ❌ INIT: FALTA RESPONDER pregunta visible ${question}`,
                                    // )
                                } else if (isVisible) {
                                    totalResponse--
                                }
                            })
                        }

                        // ✅ Validación de SiNoNa (tipos 7, 8, 18) con fields condicionales
                        if ([7, 8, 18].includes(item.input_type.type)) {
                            let secondarySelected = item.secondary_actions.find(
                                (action) =>
                                    action._id ===
                                    answerResponse.option_selected_id[0],
                            )
                            // console.log(
                            //     `   👆 [INIT] SiNoNa respondido con: "${answerResponse.option_selected_text}" (${answerResponse.option_selected_id})`,
                            // )

                            // Si el botón seleccionado tiene fields, validar que estén completos
                            if (
                                secondarySelected?.fields &&
                                secondarySelected.fields.length > 0
                            ) {
                                const hasFieldText =
                                    answerResponse.field_text?.trim() !== '' &&
                                    answerResponse.field_text !== undefined
                                const hasFieldMedia =
                                    answerResponse.field_media?.trim() !== '' &&
                                    answerResponse.field_media !== undefined
                                // console.log(
                                //     `   📝 [INIT] Requiere fields adicionales - field_text: "${answerResponse.field_text || 'vacío'}", field_media: "${answerResponse.field_media || 'vacío'}"`,
                                // )

                                if (!hasFieldText && !hasFieldMedia) {
                                    // console.log(
                                    //     `   ❌ INIT: FALTA COMPLETAR los campos adicionales (field_text o field_media)`,
                                    // )
                                    completo = false
                                } else {
                                    // console.log(`   ✅ [INIT] Fields completos`)
                                }
                            }
                        }
                    } else {
                        // console.log(
                        //     `   ❌ INIT: FALTA RESPONDER "${item.title.substring(0, 40)}"`,
                        // )
                        completo = false
                    }
                    //valido el type
                }
                if (item.status !== 3) {
                    totalQuestions++
                }
            })

            const isAllQuestionsAreMandatoryFalse =
                verifyAllQuestionsAreMandatoryFalse(newFormContent)
            console.log(
                `📊 [INIT] Todas las preguntas son opcionales: ${isAllQuestionsAreMandatoryFalse}`,
            )

            if (isAllQuestionsAreMandatoryFalse) {
                if (answers.length == 0) {
                    console.log(
                        '⚠️ [INIT] Formulario con preguntas opcionales pero sin respuestas',
                    )
                    completo = false
                }
            }

            console.log(
                `✅ [INIT RESULT] Completo: ${completo}, Habilitando botón: ${completo}`,
            )
            console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━')
            if (completo) {
                setbuttonActive(true)
            } else {
                //setbuttonActive(false)
            }

            let total = 0
            for (let index = 0; index < newFormContent.length; index++) {
                total += newFormContent[index].questionScore
            }

            let valor = 0

            if (total > 0) {
                valor = (scoreObtained * 100) / total
            }
            setData(newFormContent)
        } catch (error) {
            console.error('Error en checkCompleteInit:', error)
        } finally {
            setReloadChange()
        }
    }

    const checkComplete = useCallback(
        async (option = null) => {
            let completo = true

            setMessageFormByResponseRequest(null)
            if (option) {
                // ✅ FIX: No intentar UPDATE si el registro aún no está creado
                if (!isFormRegistered) {
                    console.log(
                        '⏳ checkComplete esperando a que el registro se cree antes de guardar respuesta',
                    )
                    // Actualizar estado local de todas formas para mostrar la selección en la UI
                    console.log(
                        '🔍 [setAnswers #4] BEFORE setAnswers callback en checkComplete (isFormRegistered=false)',
                    )
                    console.log(
                        '🔍 [setAnswers #4] Estado actual answers:',
                        JSON.stringify(answers),
                        new Date().toISOString(),
                    )
                    console.log(
                        '🔍 [setAnswers #4] Nueva respuesta a agregar:',
                        JSON.stringify(option),
                    )
                    setAnswers((prevAnswers) => {
                        console.log(
                            '🔍 [setAnswers #4 CALLBACK] prevAnswers recibido:',
                            JSON.stringify(prevAnswers),
                        )
                        const exists = prevAnswers.some(
                            (item) => item._id === option._id,
                        )
                        if (exists) {
                            return prevAnswers.map((item) =>
                                item._id === option._id
                                    ? { ...item, ...option }
                                    : item,
                            )
                        } else {
                            return [...prevAnswers, option]
                        }
                    })
                    return completo
                }

                // ✅ FIX: Guardar en BD con day_unique_id correcto
                try {
                    await updateFormAnswerSchedule({
                        newAnswer: option,
                        schedule_id,
                        form_id,
                        store_id,
                        replaceAnswers: false,
                        clearAnswers: false,
                        type: 'null',
                        day_unique_id: formDayUniqueId,
                    })

                    // ✅ Actualizar el status del formulario en el contexto inmediatamente después de guardar
                    await checkStatusProgresForm()
                } catch (error) {
                    console.error(
                        'Error en checkComplete al actualizar formulario:',
                        error,
                    )
                }

                // Actualizar estado local
                // console.log('🔍 [setAnswers #5] BEFORE setAnswers callback en checkComplete (isFormRegistered=true)')
                // console.log('🔍 [setAnswers #5] Estado actual answers:', JSON.stringify(answers), new Date().toISOString())
                // console.log('🔍 [setAnswers #5] Nueva respuesta a agregar:', JSON.stringify(option))
                setAnswers((prevAnswers) => {
                    // console.log('🔍 [setAnswers #5 CALLBACK] prevAnswers recibido:', JSON.stringify(prevAnswers))
                    const exists = prevAnswers.some(
                        (item) => item._id === option._id,
                    )
                    if (exists) {
                        return prevAnswers.map((item) =>
                            item._id === option._id ? { ...item, ...option } : item,
                        )
                    } else {
                        return [...prevAnswers, option]
                    }
                })

                // Limpiar error inline si la pregunta fue respondida
                setIncompleteQuestionIds((prev) => {
                    if (!prev.has(option._id)) return prev
                    const next = new Set(prev)
                    next.delete(option._id)
                    return next
                })
            }

            return completo
        },
        [
            formDayUniqueId,
            schedule_id,
            form_id,
            store_id,
            isFormRegistered,
            checkStatusProgresForm,
        ],
    )

    const handleInputFocus = (id) => {
        activeInputIdRef.current = id
        setActiveInputId(id)
        let index = filteredData.findIndex((item) => item._id === id)
        let viewPositionOverride: number | null = null
        console.log(
            `[KBD] handleInputFocus id=${id} → index en filteredData: ${index}`,
        )
        if (index === -1) {
            // El id no es un item top-level → buscar la pregunta padre en secondary_actions.
            // Caso 1: _id sintético con formato "{item._id}_{suffix}" (SiNoNaItem, campos con id compuesto).
            // Caso 2: field con _id real dentro de action.fields (SiNoNaItem comment field, etc).
            //   → ambos usan viewPosition=0: el top del padre al inicio de pantalla es suficiente.
            let foundIdx = filteredData.findIndex(
                (item) =>
                    (typeof id === 'string' &&
                        typeof item._id === 'string' &&
                        id.startsWith(item._id + '_')) ||
                    item.secondary_actions?.some((action) =>
                        action.fields?.some((field) => field._id === id),
                    ),
            )
            if (foundIdx !== -1) {
                index = foundIdx
                viewPositionOverride = 0 // SiNoNaItem: padre pequeño, top visible es suficiente
            } else {
                // Caso 3: id es directamente el _id de un secondary_action (ModuleShareItem type 47).
                // El padre puede ser alto con múltiples sub-inputs → viewPosition=1 muestra el bottom del padre
                // justo arriba del teclado, garantizando que los sub-inputs sean visibles.
                foundIdx = filteredData.findIndex((item) =>
                    item.secondary_actions?.some((action) => action._id === id),
                )
                if (foundIdx !== -1) {
                    index = foundIdx
                    viewPositionOverride = 1
                }
            }
            // if (!Keyboard.isVisible()) {
            //     activeInputIdRef.current?.focus()
            // }
            console.log(
                `[KBD] id=${id} no encontrado en data, buscando pregunta padre → index encontrado: ${index}`,
            )
        }
        if (index === -1 || !mainListRef.current) return

        // Siempre actualizar el último índice enfocado, independientemente del estado del teclado
        lastFocusedScrollIndexRef.current = index

        if (keyboardHeightRef.current > 0) {
            // Teclado ya abierto (switch entre inputs): esperar que measureInWindow (async) complete
            // y luego usar scroll preciso al sub-input si está disponible, o scrollToFocusedIndex como fallback.
            // Usamos keyboardHeightRef (ref sync) en vez de keyboardHidden (state async) para detectar
            // correctamente el teclado incluso cuando keyboardDidShow aún no actualizó el state de React.
            setTimeout(() => {
                const subInputMeasure = pendingSubInputScreenYRef.current
                pendingSubInputScreenYRef.current = null
                if (subInputMeasure) {
                    const screenH = Dimensions.get('window').height
                    const kbHeight = keyboardHeightRef.current
                    const margin = Platform.OS === 'android' ? 60 : 12
                    const targetScreenY =
                        screenH - kbHeight - subInputMeasure.h - margin
                    const delta = subInputMeasure.y - targetScreenY
                    const newOffset = Math.max(
                        0,
                        currentScrollOffsetRef.current + delta,
                    )
                    console.log(
                        `[KBD] subInput scroll (kb open) | screenY=${subInputMeasure.y.toFixed(0)} targetY=${targetScreenY.toFixed(0)} delta=${delta.toFixed(0)} newOffset=${newOffset.toFixed(0)}`,
                    )
                    isProgrammaticScrollRef.current = true
                    mainListRef.current?.scrollToOffset({
                        offset: newOffset,
                        animated: true,
                    })
                } else {
                    scrollToFocusedIndex(index, viewPositionOverride)
                }
            }, 150)
        } else {
            // Teclado cerrado: guardar índice. keyboardDidShow disparará el scroll
            // una vez que el padding ya esté aplicado y el teclado completamente visible.
            console.log(
                `[KBD] focus en index=${index} con teclado cerrado → esperando keyboardDidShow`,
            )
            pendingScrollIndexRef.current = index
            pendingScrollViewPositionRef.current = viewPositionOverride
        }
    }

    // Llamado desde ModuleShareItem cuando un sub-input hace focus y su posición en pantalla es conocida.
    // Guarda la posición para que keyboardDidShow haga scroll preciso al sub-input en vez de al padre.
    const handleSubInputMeasure = (id: string, screenY: number, inputH: number) => {
        console.log(
            `[KBD] handleSubInputMeasure id=${id} screenY=${screenY.toFixed(0)} inputH=${inputH.toFixed(0)}`,
        )
        pendingSubInputScreenYRef.current = { y: screenY, h: inputH }
    }

    const validatedComplet = (data) => {
        // console.log('🚀🚀🚀🚀🚀🚀🚀🚀🚀 ~ validatedComplet ~ data:', data.length)
        console.log(
            '📋 [VALIDATION] ═════════ Iniciando validación de formulario ═════════',
        )
        let completo = true
        let totalResponse = data?.length
        let totalQuestions = 0
        let questions_filter = data.filter((item) => {
            if (!('hideQuestion' in item)) {
                return true
            }
            return item.hideQuestion != null && item.hideQuestion == false
        })
        // console.log(
        //     `📋 [VALIDATION] Total preguntas: ${data.length}, Preguntas visibles: ${questions_filter.length}`,
        // )
        // console.log(`📋 [VALIDATION] Total respuestas actuales: ${answers.length}`)
        questions_filter.forEach((item, index) => {
            const isMandatory = item.mandatory !== false
            const inputType = item.input_type.type

            // ✅ Los inputs tipo 38 (input_notedit) vienen pre-cargados y no deben bloquear el envío
            // Son campos no editables que muestran valores del backend
            if (inputType === 38) {
                return // Saltar validación de este input
            }

            // si es status 3 es una pregunta que se debe ocultar, entonces no la valido a menos que tenga la key hideQuestion en false, porque hay casos que una pregunta tiene status 3 pero se muestra igual porque hideQuestion es false, entonces la valido normalmente
            const validHideQuestion =
                item.status !== 3 || item.hideQuestion === false

            if ((item.requiered || validHideQuestion) && isMandatory) {
                let answerResponse = answers.find(
                    (answer) => answer._id === item._id,
                )
                // console.log(
                //     `🔍 [Q${index}] "${item.title}" (${item._id}) - Type: ${inputType}, hideQuestion: ${item.hideQuestion}, Respondida: ${!!answerResponse}`,
                // )

                if (answerResponse !== undefined) {
                    // Validación de inputs con acciones secundarias
                    if (inputType === 14) {
                        let secondarySelected = item.secondary_actions.find(
                            (action) =>
                                action._id === answerResponse.option_selected_id[0],
                        )
                        // console.log(
                        //     `   🔽 Dropdown respondido con: "${answerResponse.option_selected_text}" (${answerResponse.option_selected_id})`,
                        // )
                        if (
                            secondarySelected?.show_questions &&
                            secondarySelected?.show_questions.length > 0
                        ) {
                            // console.log(
                            //     `   📌 Debe mostrar ${secondarySelected.show_questions.length} preguntas condicionales:`,
                            //     secondarySelected.show_questions,
                            // )
                            secondarySelected?.show_questions.forEach((question) => {
                                // ✅ Solo validar si la pregunta está visible
                                const questionItem = questions_filter.find(
                                    (q) => q._id === question,
                                )
                                const isVisible =
                                    questionItem &&
                                    questionItem.hideQuestion !== true
                                let contesto = answers.find(
                                    (answer) => answer._id == question,
                                )
                                console.log(
                                    `      ↳ Pregunta ${question}: Visible=${isVisible}, Respondida=${!!contesto}`,
                                )
                                if (isVisible && contesto == undefined) {
                                    // console.log(
                                    //     `      ❌ FALTA RESPONDER: Pregunta visible ${question} no tiene respuesta`,
                                    // )
                                    completo = false
                                } else if (isVisible) {
                                    totalResponse--
                                }
                            })
                        } else {
                            totalResponse--
                        }
                    }

                    // ✅ Validación de SiNoNa (tipos 7, 8, 18) con fields condicionales
                    if ([7, 8, 18].includes(inputType)) {
                        let secondarySelected = item.secondary_actions.find(
                            (action) =>
                                action._id === answerResponse.option_selected_id[0],
                        )
                        // console.log(
                        //     `   👆 SiNoNa respondido con: "${answerResponse.option_selected_text}" (${answerResponse.option_selected_id})`,
                        // )

                        // Si el botón seleccionado tiene fields, validar que estén completos
                        if (
                            secondarySelected?.fields &&
                            secondarySelected.fields.length > 0
                        ) {
                            const hasFieldText =
                                answerResponse.field_text?.trim() !== '' &&
                                answerResponse.field_text !== undefined
                            const hasFieldMedia =
                                answerResponse.field_media?.trim() !== '' &&
                                answerResponse.field_media !== undefined
                            // console.log(
                            //     `   📝 Requiere fields adicionales - field_text: "${answerResponse.field_text || 'vacío'}", field_media: "${answerResponse.field_media || 'vacío'}"`,
                            // )

                            if (!hasFieldText && !hasFieldMedia) {
                                // console.log(
                                //     `   ❌ FALTA COMPLETAR: Los campos adicionales están vacíos (field_text o field_media)`,
                                // )
                                completo = false
                            } else {
                                // console.log(`   ✅ Fields completos`)
                            }
                        }
                    }

                    // Validación de inputs de texto (5: Comment, 9: Number)
                    if ([5, 9].includes(inputType)) {
                        const textValue = answerResponse.questionInput?.trim() || ''
                        if (textValue === '') {
                            // console.log('falta responder esta interna...', answerResponse)
                            completo = false
                        }
                    }

                    // Validación de inputs de texto (21: PriceNa)
                    if (inputType === 21) {
                        const textValue = answerResponse.questionInput?.trim() || ''
                        const optionSelected =
                            answerResponse.questionOptionSelected || []
                        if (textValue === '' && optionSelected.length === 0) {
                            // console.log('falta responder esta interna...', answerResponse)
                            completo = false
                        }
                    }
                    // Validación de module_share (47)
                    if (inputType === 47) {
                        const parentInput =
                            answerResponse.questionInput?.trim() ||
                            answerResponse.question_input?.trim() ||
                            ''
                        const expectedModules = item.secondary_actions?.length ?? 0
                        const savedModules: any[] =
                            answerResponse.response_inputs_modules ?? []

                        const parentFilled = parentInput !== ''
                        const allModulesFilled =
                            savedModules.length === expectedModules &&
                            savedModules.every((m) => {
                                const v =
                                    m.questionInput?.trim() ||
                                    m.question_input?.trim() ||
                                    ''
                                return v !== ''
                            })

                        console.log(
                            `   📦 ModuleShare - padre: "${parentInput}", módulos: ${savedModules.length}/${expectedModules}, todos llenos: ${allModulesFilled}`,
                        )

                        if (!parentFilled || !allModulesFilled) {
                            console.log(
                                `   ❌ FALTA COMPLETAR: ModuleShare incompleto`,
                            )
                            completo = false
                        }
                    }

                    // mas validaciones por type ...
                } else {
                    // console.log(
                    //     `   ❌ FALTA RESPONDER: "${item.title}" no tiene respuesta`,
                    // )
                    completo = false
                }
                //valido el type
            }
            if (item.status !== 3) {
                totalQuestions++
            }
        })

        const isAllQuestionsAreMandatoryFalse =
            verifyAllQuestionsAreMandatoryFalse(questions_filter)
        // console.log(
        //     `📊 [VALIDATION] Todas las preguntas son opcionales: ${isAllQuestionsAreMandatoryFalse}`,
        // )

        if (isAllQuestionsAreMandatoryFalse) {
            if (answers.length == 0) {
                console.log(
                    '⚠️ [VALIDATION] Formulario con preguntas opcionales pero sin respuestas',
                )
                completo = false
            }
        }
        // console.log(
        //     `✅ [VALIDATION RESULT] Completo: ${completo}, Total respuestas: ${answers.length}`,
        // )
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━')
        setbuttonActive(true)
        if (completo) {
            setReloadChange()
        }
    }

    // Igual lógica que validatedComplet pero devuelve los IDs incompletos (no modifica estado)
    const validateAndGetIncomplete = (data): Set<string> => {
        const incomplete = new Set<string>()

        const questions_filter = data.filter((item) => {
            if (!('hideQuestion' in item)) return true
            return item.hideQuestion != null && item.hideQuestion == false
        })

        questions_filter.forEach((item) => {
            const isMandatory = item.mandatory !== false
            const inputType = item.input_type.type
            const validHideQuestion =
                item.status !== 3 || item.hideQuestion === false

            if (inputType === 38) return

            if ((item.requiered || validHideQuestion) && isMandatory) {
                const answerResponse = answers.find(
                    (answer) => answer._id === item._id,
                )

                if (answerResponse === undefined) {
                    incomplete.add(item._id)
                    return
                }

                if ([7, 8, 18].includes(inputType)) {
                    const secondarySelected = item.secondary_actions.find(
                        (action) =>
                            action._id === answerResponse.option_selected_id[0],
                    )
                    const hasFields = (secondarySelected?.fields?.length ?? 0) > 0
                    const hasFieldText =
                        answerResponse.field_text?.trim() !== '' &&
                        answerResponse.field_text !== undefined
                    const hasFieldMedia =
                        answerResponse.field_media?.trim() !== '' &&
                        answerResponse.field_media !== undefined
                    if (hasFields && !hasFieldText && !hasFieldMedia)
                        incomplete.add(item._id)
                }

                if (inputType === 14) {
                    const secondarySelected = item.secondary_actions.find(
                        (action) =>
                            action._id === answerResponse.option_selected_id[0],
                    )
                    if (secondarySelected?.show_questions?.length > 0) {
                        secondarySelected.show_questions.forEach((questionId) => {
                            const questionItem = questions_filter.find(
                                (q) => q._id === questionId,
                            )
                            const isVisible =
                                questionItem && questionItem.hideQuestion !== true
                            const answered = answers.find(
                                (answer) => answer._id == questionId,
                            )
                            if (isVisible && !answered) incomplete.add(questionId)
                        })
                    }
                }

                if ([5, 9].includes(inputType)) {
                    const textValue = answerResponse.questionInput?.trim() || ''
                    if (textValue === '') incomplete.add(item._id)
                }

                if (inputType === 21) {
                    const textValue = answerResponse.questionInput?.trim() || ''
                    const optionSelected =
                        answerResponse.questionOptionSelected || []
                    if (textValue === '' && optionSelected.length === 0)
                        incomplete.add(item._id)
                }

                if (inputType === 47) {
                    const parentInput =
                        answerResponse.questionInput?.trim() ||
                        answerResponse.question_input?.trim() ||
                        ''
                    const expectedModules = item.secondary_actions?.length ?? 0
                    const savedModules: any[] =
                        answerResponse.response_inputs_modules ?? []
                    const allModulesFilled =
                        savedModules.length === expectedModules &&
                        savedModules.every(
                            (m) =>
                                (m.questionInput?.trim() ||
                                    m.question_input?.trim() ||
                                    '') !== '',
                        )
                    if (!parentInput || !allModulesFilled) incomplete.add(item._id)
                }
            }
        })

        return incomplete
    }

    const verifyAllQuestionsAreMandatoryFalse = (questions) => {
        let allMandatoryFalse = true
        for (const question of questions) {
            if (
                question.mandatory == null ||
                question.mandatory == undefined ||
                question.mandatory === true
            ) {
                allMandatoryFalse = false
                break
            }
        }
        return allMandatoryFalse
    }

    const isQuestionAllAnswered = async (
        questions: FormContentsModel[],
        schedule_id: string,
        form_id: string,
        store_id: string,
    ): Promise<boolean> => {
        let completo: boolean = true

        // si no tengo ninguna pregunta con mandatory false
        // no hace falta validar nada return true
        const isQuestionIncludeMandatoryFalse = questions.some(
            (question) => question.mandatory === false,
        )

        if (!isQuestionIncludeMandatoryFalse) {
            return completo
        }

        const { answers } = await getFormsAnswersInStore(
            schedule_id,
            form_id,
            store_id,
        )

        const cantAnswers = answers.length
        const cantQuestions = questions.length

        if (cantAnswers === cantQuestions) {
            return completo
        } else {
            completo = false
        }

        return completo
    }

    const confirmSendForm = async () => {
        try {
            let var_progress_questions_yes_na = null

            console.log(
                '[confirmSendForm] - guardo en el local porque no hay conexion',
                schedule_id,
                form_id,
                store_id,
            )

            // ✅ FIX #1: Validar que hay respuestas antes de enviar
            // Obtener formulario actual para validar answers
            const currentForm = await getFormsAnswersInStore(
                schedule_id,
                form_id,
                store_id,
                null,
                repeat,
            )
            // console.log('🔍 [confirmSendForm] Formulario actual obtenido de BD para validación - currentForm => ', currentForm )
            const currentFqaId = currentForm?.fqa_id

            if (currentForm && currentForm.answers) {
                const answersArray = Array.isArray(currentForm.answers)
                    ? currentForm.answers
                    : []
                // ✅ FIX: currentForm.questions ya viene como objeto/array desde getFormsAnswersInStore
                // No necesita JSON.parse porque la BD ya lo parsea (ver schedules.database.js:4133)
                const questionsArray = Array.isArray(currentForm.questions)
                    ? currentForm.questions
                    : []

                // Verificar si hay preguntas obligatorias
                const hasMandatory = questionsArray.some((q) => q.mandatory === true)

                // Si hay preguntas obligatorias y no hay respuestas, mostrar error
                if (answersArray.length === 0 && hasMandatory) {
                    console.error(
                        '❌ Intento de enviar formulario sin respuestas obligatorias',
                    )
                    Alert.alert(
                        languageFile?.incomplete_form || 'Formulario incompleto',
                        languageFile?.must_answer_questions ||
                            'Debes responder al menos una pregunta obligatoria antes de enviar.',
                        [{ text: 'OK' }],
                    )
                    return
                }

                console.log(
                    `✅ Validación OK: ${answersArray.length} respuestas de ${questionsArray.length} preguntas`,
                )
            }

            // Si es formulario de creación de heladera, crear el registro vacío ANTES de enviar
            if (isFormCreateRefrigerator) {
                removeCacheByFragment('get_store')
            }

            const date_finish = getDateNow()

            const registerUpdated =
                await schedulesServices.updateStatusFormAnswerSchedule(
                    FORM_SCHEDULE_STATUS.PENDING_CONNECTION,
                    schedule_id,
                    form_id,
                    store_id,
                    'sin conexion',
                    currentFqaId,
                    checkin_id,
                )

            await schedulesServices.updateStatusSendForm(
                FORM_SCHEDULE_STATUS.PENDING_CONNECTION,
                schedule_id,
                form_id,
                store_id,
                'sin conexion',
                registerUpdated.updatedId,
                date_finish,
            )

            try {
                //actualizo el form adv
                if (form_id && form_id != '') {
                    console.log(
                        'registerUpdated.updatedId => ',
                        registerUpdated.updatedId,
                    )
                    const {
                        answers,
                        date_init,
                        date_finish,
                        progress_questions_yes_na,
                        heladera,
                        create_refrigerator,
                        type_form,
                        form_id_father,
                        scoreObtained,
                    } = await getFormsAnswersInStore(
                        schedule_id,
                        form_id,
                        store_id,
                        registerUpdated.updatedId,
                    )
                    var_progress_questions_yes_na = progress_questions_yes_na

                    console.log(
                        'progress_questions_yes_na => ',
                        progress_questions_yes_na,
                    )
                    console.log('scoreObtained => ', scoreObtained)
                    // ✅ Guardar como PENDING_CONNECTION porque aún no se sincronizó con el servidor
                    await retryDbOperation(async () => {
                        return await schedulesServices.upsertFormStatus(
                            schedule_id,
                            form_id,
                            FORM_SCHEDULE_STATUS.PENDING_CONNECTION,
                            getDateNow(),
                            progress_questions_yes_na,
                            scoreObtained,
                        )
                    })
                }
            } catch (error) {}

            // ✅ FIX: Actualizar contexto PRIMERO, antes de otras operaciones
            // Esto garantiza que el estado esté disponible cuando StoreDetail vuelva a renderizar
            console.log(
                '🔄 [confirmSendForm] Actualizando status a PENDING_CONNECTION en contexto',
            )
            updateStatusProgressForm(
                formsContext,
                schedule_id,
                form_id,
                store_id,
                FORM_SCHEDULE_STATUS.PENDING_CONNECTION,
                var_progress_questions_yes_na,
            )

            // ✅ Dar tiempo a React para propagar el estado actualizado
            await new Promise((resolve) => setTimeout(resolve, 100))

            // ✅ FIX REPEAT: Crear nuevo registro vacío para el siguiente intento
            if (repeat === 1 && store_id) {
                try {
                    console.log(
                        '🔄 [confirmSendForm] Repeat form: creando nuevo registro vacío para siguiente intento',
                    )
                    const newEmptyRecord = await saveFormAnswers({
                        schedule_id,
                        form_id,
                        store_id,
                        repeat: 1,
                        status: FORM_SCHEDULE_STATUS.IN_PROGRESS, //FORM_SCHEDULE_STATUS.PENDING, inicializo el form repeat en status en curso
                        answers: '[]',
                        questions: currentForm?.questions
                            ? JSON.stringify(currentForm.questions)
                            : '[]',
                        progress: false,
                        totalScore: 0,
                        name_form: currentForm?.name_form || name_form,
                        type_form: currentForm?.type_form || formTypeObj,
                        heladera: currentForm?.heladera,
                        create_refrigerator: currentForm?.create_refrigerator,
                        form_id_father: currentForm?.form_id_father,
                        checkin_id,
                        checkout: 1,
                    })
                    console.log(
                        '✅ [confirmSendForm] Repeat form: nuevo registro vacío creado',
                        {
                            new_record_id: newEmptyRecord?.result?._id,
                            submitted_record_id: currentFqaId,
                        },
                    )
                } catch (repeatError) {
                    console.warn(
                        '⚠️ [confirmSendForm] Error creando registro vacío para repeat form:',
                        repeatError,
                    )
                }
            }

            // Si es CREACIÓN de heladera (isCreateHeladera === true), enviamos inmediatamente
            // al backend y esperamos la respuesta antes de hacer goBack
            if (isCreateHeladera === true) {
                console.log(
                    '🚀 isCreateHeladera: Enviando formulario inmediatamente al backend...',
                )
                try {
                    const sendResult = await sendForm(
                        FORM_SCHEDULE_STATUS.PENDING_CONNECTION,
                        undefined,
                        schedule_id,
                        form_id,
                        store_id,
                        () => {}, // No navegar automáticamente, lo hacemos nosotros después
                        registerUpdated.updatedId,
                    )
                    console.log(
                        '✅ Formulario de creación de heladera enviado:',
                        sendResult,
                    )

                    // ✅ FIX HELADERA: Eliminar el registro local después del envío exitoso
                    // Como ya se envió al backend, no necesitamos el registro local
                    if (registerUpdated.updatedId) {
                        console.log(
                            '🗑️ Eliminando registro local de creación de heladera:',
                            registerUpdated.updatedId,
                        )
                        await schedulesServices.deleteFqaById(
                            registerUpdated.updatedId,
                        )
                    }

                    // Fetch get_store para obtener la data más reciente (incluye la nueva heladera tipo 37)
                    // y actualizar formsContext ANTES de navegar hacia atrás
                    try {
                        const session = await fetchAuthSession()
                        const authToken =
                            session.tokens?.accessToken?.toString() || ''
                        const { existsInDB, _id_mongoDB } =
                            await schedulesServices.getExistsInDB(store_id)

                        if (authToken && existsInDB) {
                            const storeResponse = await request_get_store({
                                authToken,
                                store_id: _id_mongoDB ? _id_mongoDB : store_id,
                                schedule_id,
                            })

                            if (
                                storeResponse?.status &&
                                storeResponse?.response?.status == 1
                            ) {
                                const storeData = storeResponse.response.single
                                if (
                                    storeData?.forms_pos &&
                                    storeData.forms_pos.length > 0
                                ) {
                                    const freshForms = storeData.forms_pos
                                    console.log(
                                        '✅ [confirmSendForm] get_store devolvió forms_pos con',
                                        freshForms.length,
                                        'formularios',
                                    )

                                    // Guardar en SQLite (misma lógica que saveFormsHeladeras en StoreDetail)
                                    for (const form of freshForms) {
                                        try {
                                            await schedulesServices.addForm(
                                                form,
                                                false,
                                                true,
                                            )
                                            if (
                                                form.contents &&
                                                form.contents.length > 0
                                            ) {
                                                for (const content of form.contents) {
                                                    if (
                                                        content.secondary_actions?.some(
                                                            (a) =>
                                                                a.form?.length > 0,
                                                        )
                                                    ) {
                                                        for (const action of content.secondary_actions) {
                                                            if (
                                                                action?.form
                                                                    ?.length > 0
                                                            ) {
                                                                const create_refrigerator =
                                                                    content
                                                                        .input_type
                                                                        ?.type === 16
                                                                await schedulesServices.addForm(
                                                                    action.form[0],
                                                                    false,
                                                                    true,
                                                                    create_refrigerator,
                                                                    form._id,
                                                                )
                                                            }
                                                        }
                                                    }
                                                }
                                            }
                                        } catch (e) {
                                            console.warn(
                                                '⚠️ Error guardando form heladera en SQLite:',
                                                e,
                                            )
                                        }
                                    }

                                    // Actualizar formsContext con los forms frescos del servidor
                                    setFormsContext((prevFormsContext) => {
                                        const updatedContext = [...prevFormsContext]
                                        for (const formHeladera of freshForms) {
                                            const existingIndex =
                                                updatedContext.findIndex(
                                                    (f) =>
                                                        f._id === formHeladera._id,
                                                )
                                            if (existingIndex !== -1) {
                                                updatedContext[existingIndex] = {
                                                    ...updatedContext[existingIndex],
                                                    contents: formHeladera.contents,
                                                }
                                            } else {
                                                updatedContext.push({
                                                    ...formHeladera,
                                                    store_id,
                                                    schedule_id,
                                                })
                                            }
                                        }
                                        return updatedContext
                                    })
                                    console.log(
                                        '✅ [confirmSendForm] formsContext actualizado con data fresca del servidor',
                                    )
                                }
                            }
                        }
                    } catch (fetchError) {
                        console.warn(
                            '⚠️ [confirmSendForm] Error fetching get_store después de crear heladera, se usará reload como fallback:',
                            fetchError,
                        )
                        setReloadFormsFn()
                    }

                    onFormSubmitted ? onFormSubmitted() : navigation.goBack()
                    return // Salir de la función, ya navegamos
                } catch (error) {
                    console.error(
                        '❌ Error enviando formulario de creación de heladera:',
                        error,
                    )
                    // Si falla, continúa con el flujo normal (diferido)
                }
            } else if (isFormCreateRefrigerator) {
                // Para otros casos de heladera (relevar existente), solo notificamos
                console.log(
                    '🔄🔄🔄🔄🔄🔄🔄🔄 isFormCreateRefrigerator: Disparando reload de formularios en StoreDetail',
                )
                const active = false
                console.log('params ===========> ', {
                    form_id,
                    question_id_param,
                    form_id_father_param,
                })
                removeCacheByFragment('get_store')
                updateActveFormRefrigerator(
                    form_refrigerators_main,
                    form_id_father_param,
                    question_id_param,
                    active,
                )
                setReloadFormsFn()

                console.log(
                    '🔄🔄🔄🔄🔄 isFormCreateRefrigerator: despues de remover cache, updatear active y disparar reload',
                )
            }

            if (
                schedule_id &&
                form_id &&
                store_id &&
                !isFormIncidence &&
                !isFormAlias
            ) {
                // console.log('envio fooorrrrm y actualizo adv~ schedule_id:', schedule_id)

                // ✅ FIX: Usar PENDING_CONNECTION en lugar de 1 (COMPLETED)
                // El formulario solo debe marcarse como COMPLETED cuando se envía exitosamente al servidor
                const result = await schedulesServices.upsertScheduleAdvanceLocal(
                    schedule_id,
                    store_id,
                    FORM_SCHEDULE_STATUS.PENDING_CONNECTION,
                    new Date().toISOString(),
                    [
                        {
                            _id: form_id,
                            progress: var_progress_questions_yes_na,
                            status: FORM_SCHEDULE_STATUS.PENDING_CONNECTION,
                        },
                    ],
                    [],
                )

                // console.log('result => ', result)
            } else {
                console.log(
                    'no guardo el adv falta datos, ',
                    schedule_id,
                    form_id,
                    store_id,
                    isFormIncidence,
                )
            }

            let statusIncidence = null
            let incidenceAnswerText = null
            if (isFormIncidence === true) {
                const status = getStatusByReportSelected(answers)
                const incidenceResponse = getResponseIncidence(answers)

                statusIncidence = status
                incidenceAnswerText = incidenceResponse
            }

            if (actionButonSend != undefined) {
                console.log('estoyyy acaaaaa antes del goooo: ', statusIncidence)
                console.log('estoyyy acaaaaa antes del goooo: ', statusIncidence)
                actionButonSend(statusIncidence, incidenceAnswerText)
            } else {
                if (hasFinishSheet) {
                    console.log(' 1estoyyy acaaaaa antes del openFinishSheet')
                    openFinishSheet(
                        clearRegisterDatabaseLocal
                            ? async () => {
                                  setIsLoadingReload(true)
                                  if (schedule_id === 'wise') {
                                      console.log(
                                          '📝 WISE: Formulario guardado como pendiente, creando nuevo registro limpio',
                                      )
                                      await clearRegisterDatabaseLocal('reload')
                                  } else {
                                      clearRegisterDatabaseLocal('pending')
                                  }
                                  setIsLoadingReload(false)
                              }
                            : undefined,
                    )
                } else {
                    console.log(' 2estoyyy acaaaaa antes del goBack')
                    onFormSubmitted ? onFormSubmitted() : navigation.goBack()
                }
            }
        } catch (error) {
            console.error('Error en confirmSendForm:', error)
            throw error
        } finally {
            // closeBottomSheet(true)
        }
    }

    const isFormSendNow = (formType: number): 0 | 1 => {
        // revisar que formularios necesitan enviarse ya o cuales pueden esperar
        // 1 envio ya, 0 envio despues

        const mapForms = {
            [FORM_TYPES.FORM_ALIAS]: 1,
            // [FORM_TYPES.FORM_INCIDENCE]: 1,
        }
        return (mapForms[formType] || 0) as 0 | 1
    }

    const getStatusByReportSelected = (answers: ModelAnswerFromProps[]): number => {
        if (!answers || answers.length == 0) return STORE_STATUS.INCIDENCE_REPORTED

        const answer = answers.find(
            (answer) => answer.question_id == QUESTION_ID_CLOSE_MOTIVE,
        )

        if (answer) {
            if (answer.option_selected_id.includes(OPTION_CLOSE_PERMANENTLY)) {
                return STORE_STATUS.INCIDENCE_REPORTED_PERMANENTE
            }
            if (answer.option_selected_id.includes(OPTION_CLOSE)) {
                return STORE_STATUS.INCIDENCE_REPORTED_CLOSE
            }
            return STORE_STATUS.INCIDENCE_REPORTED
        }
    }

    const getResponseIncidence = (answers: ModelAnswerFromProps[]) => {
        const RESPONSE_DEFAULT = 'Cliente cerrado'

        if (!answers || answers.length == 0) return 'Cliente cerrado'

        const answer = answers.find(
            (answer) => answer.question_id == QUESTION_ID_CLOSE_MOTIVE,
        )

        if (answer) {
            if (
                answer.option_selected_text &&
                answer.option_selected_text.length > 0
            ) {
                return answer.option_selected_text[0]
            }
        }

        return RESPONSE_DEFAULT
    }

    /**
     * @description Funcion que ejecuta el boton "Enviar" del formulario
     */
    const handleSendFormNew = async () => {
        try {
            // Validar antes de enviar; si hay incompletos, marcarlos y salir
            const incompleteIds = validateAndGetIncomplete(data)
            if (incompleteIds.size > 0) {
                setIncompleteQuestionIds(incompleteIds)
                // Scroll a la primera pregunta incompleta
                const firstIncompleteIndex = filteredData.findIndex((item: any) =>
                    incompleteIds.has(item._id),
                )
                if (firstIncompleteIndex !== -1 && mainListRef.current) {
                    setTimeout(() => {
                        mainListRef.current?.scrollToIndex({
                            index: firstIncompleteIndex,
                            animated: true,
                            viewPosition: 0.3,
                        })
                    }, 100)
                }
                return
            }
            setIncompleteQuestionIds(new Set())

            // console.log('🚀 handleSendFormNew: Iniciando proceso de envío de formulario... ', { formType, schedule_id, form_id, store_id })
            // console.log('🔍 [setAnswers #7] BEFORE setAnswers([]) en handleSendFormNew - Estado previo:', JSON.stringify(answers))
            // console.log('🔍 [setAnswers #7] Limpiando estado después de enviar formulario', new Date().toISOString())
            setAnswers([])
            console.log(
                '🔍 [setAnswers #7] AFTER setAnswers([]) - Estado debería estar vacío',
            )
            if (messageFormByResponseRequest != null) {
                setMessageFormByResponseRequest(null)
            }
            const hasConnection = await validateConnectionNetwork()

            if (formType !== FORM_TYPES.FORM_POP_HELADERA) {
                const isCompletedForm = await isQuestionAllAnswered(
                    formContent,
                    schedule_id,
                    form_id,
                    store_id,
                )

                if (!isCompletedForm) {
                    const title = '¿Estás seguro que deseas enviar?'
                    const description =
                        'Hay preguntas sin responder. Una vez enviado, no podrás completar este formulario nuevamente hasta mañana.'
                    const snapPoints = ['55%']

                    const buttonCancel = languageFile.cancel
                    const buttonConfirm = languageFile.confirm

                    const handleConfirmSendFormModal = async () => {
                        if (isSendingForm.current) {
                            console.error(
                                'Ya se está enviando el formulario, por favor espera.',
                            )
                            return
                        }

                        try {
                            isSendingForm.current = true
                            await confirmSendForm()
                        } catch (error) {
                            console.error('Error en confirmSendForm:', error)
                            // Solo cerrar en error: en caso de éxito, confirmSendForm llama
                            // handleFormSubmitted que ya reemplaza (o cierra) el sheet.
                            // Cerrar acá en éxito pisaba el sheet de navegación al siguiente form.
                            if (!hasFinishSheet) {
                                closeBottomSheet(true)
                            }
                        } finally {
                            isSendingForm.current = false
                        }
                    }

                    openBottomSheet(
                        <BottomSheetAlert
                            image={require('../../../assets/stores/checkOutLocked.png')}
                            title={title}
                            desc={description}
                            button={buttonCancel}
                            closeBottomSheet={closeBottomSheet}
                            buttonGoto={buttonConfirm}
                            goto={handleConfirmSendFormModal}
                        />,
                    )
                } else {
                    const isSendNow = isFormSendNow(formType)

                    console.log('isSendNow, formType', isSendNow, formType)

                    if (isSendNow === 1) {
                        console.log('------------ enviar ahora------------ ')
                        if (hasConnection) {
                            await sendFormNow()
                        } else {
                            executeLoginByFormLostConnection(formType)
                        }
                    } else {
                        await confirmSendForm()
                    }
                }

                return
            }

            if (formType == FORM_TYPES.FORM_POP_HELADERA) {
                // es de tipo heladera
                console.log(schedule_id)
                console.log(form_id)
                console.log(store_id)

                // ✅ FIX: Usar PENDING_CONNECTION en lugar de COMPLETED
                // El formulario solo debe pasar a COMPLETED cuando se envíe exitosamente al servidor
                await schedulesServices.updateStatusFormAnswerSchedule(
                    FORM_SCHEDULE_STATUS.PENDING_CONNECTION,
                    schedule_id,
                    form_id,
                    store_id,
                    'sin conexion',
                    null,
                    checkin_id,
                )

                const index = formsContext.findIndex(
                    (form) => form._id.toString() === form_id.toString(),
                )

                if (index !== -1) {
                    const updatedForms = [...formsContext]

                    updatedForms[index] = {
                        ...updatedForms[index],
                        status: FORM_SCHEDULE_STATUS.PENDING_CONNECTION, // ✅ PENDING_CONNECTION (5) = completo pero falta enviar
                    }

                    setFormsContext(updatedForms)
                    setReloadChange()

                    onFormSubmitted ? onFormSubmitted() : navigation.goBack()
                }
            }

            // if (formType == FORM_TYPES.FORM_REFRIGERATOR) {

            // }

            // if (formType !== FORM_TYPES.FORM_POP_HELADERA) {
            //     const isCompletedForm = await isQuestionAllAnswered(
            //         formContent,
            //         schedule_id,
            //         form_id,
            //         store_id,
            //     )

            //     if (!isCompletedForm) {
            //         const title = '¿Estás seguro que deseas enviar?'
            //         const description =
            //             'Hay preguntas sin responder. Una vez enviado, no podrás completar este formulario nuevamente hasta mañana.'
            //         const snapPoints = ['55%']

            //         const buttonCancel = languageFile.cancel
            //         const buttonConfirm = languageFile.confirm

            //         const handleConfirmSendFormModal = async () => {
            //             if (isSendingForm.current) {
            //                 console.error(
            //                     'Ya se está enviando el formulario, por favor espera.',
            //                 )
            //                 return
            //             }

            //             try {
            //                 isSendingForm.current = true
            //                 await confirmSendForm()
            //             } catch (error) {
            //                 console.error('Error en confirmSendForm:', error)
            //             } finally {
            //                 // ✅ FIX #2: Reset flag siempre, incluso si hay error
            //                 isSendingForm.current = false
            //                 closeBottomSheet(true)
            //             }
            //         }

            //         openBottomSheet(
            //             <BottomSheetAlert
            //                 image={require('../../../assets/stores/checkOutLocked.png')}
            //                 title={title}
            //                 desc={description}
            //                 button={buttonCancel}
            //                 closeBottomSheet={closeBottomSheet}
            //                 buttonGoto={buttonConfirm}
            //                 goto={handleConfirmSendFormModal}
            //             />,
            //         )
            //     } else {
            //         const isSendNow = isFormSendNow(formType)

            //         console.log('isSendNow, formType', isSendNow, formType)

            //         if (isSendNow === 1) {
            //             console.log('------------ enviar ahora------------ ')
            //             if (hasConnection) {
            //                 await sendFormNow()
            //             } else {
            //                 executeLoginByFormLostConnection(formType)
            //             }
            //         } else {
            //             await confirmSendForm()
            //         }
            //     }
            // } else {
            //     // es de tipo heladera
            //     console.log(schedule_id)
            //     console.log(form_id)
            //     console.log(store_id)

            //     // ✅ FIX: Usar PENDING_CONNECTION en lugar de COMPLETED
            //     // El formulario solo debe pasar a COMPLETED cuando se envíe exitosamente al servidor
            //     await schedulesServices.updateStatusFormAnswerSchedule(
            //         FORM_SCHEDULE_STATUS.PENDING_CONNECTION,
            //         schedule_id,
            //         form_id,
            //         store_id,
            //     )

            //     const index = formsContext.findIndex(
            //         (form) => form._id.toString() === form_id.toString(),
            //     )

            //     if (index !== -1) {
            //         const updatedForms = [...formsContext]

            //         updatedForms[index] = {
            //             ...updatedForms[index],
            //             status: FORM_SCHEDULE_STATUS.PENDING_CONNECTION, // ✅ PENDING_CONNECTION (5) = completo pero falta enviar
            //         }

            //         setFormsContext(updatedForms)
            //         setReloadChange()

            //         navigation.goBack()
            //     }
            // }
        } catch (error) {
            console.error('Error en handleSendFormNew:', error)
        } finally {
            // ✅ FIX #2: Reset flag siempre para permitir reintentos
            isSendingForm.current = false
            Keyboard.dismiss()
        }
    }

    const sendFormNow = async () => {
        try {
            const {
                answers,
                date_init,
                date_finish,
                progress_questions_yes_na,
                heladera,
                create_refrigerator,
                type_form,
                scoreObtained,
                form_id_father,
            } = await getFormsAnswersInStore(schedule_id, form_id, store_id)

            let obj_json_raw = {
                answers: answers,
                date_finish: isValidDateString(date_finish)
                    ? date_finish
                    : date_init,
                date_init: date_init,
                id_activity: form_id,
                status: 0,
                progress: progress_questions_yes_na,
                score: scoreObtained,
                heladera: heladera,
                create_refrigerator: create_refrigerator,
                input_type: type_form ? type_form['type'] : null,
                refrigerator_id: '',
                store_id: store_id,
            }

            // ✅ Si es formulario hijo (tiene form_id_father), el form_id ES el refrigerator_id
            if (form_id_father && form_id_father !== '' && form_id_father != '0') {
                console.log('📋 Formulario hijo detectado:', {
                    form_id,
                    form_id_father,
                    heladera,
                })
                obj_json_raw.id_activity = form_id_father // Enviar ID del padre
                obj_json_raw.refrigerator_id = form_id // El form_id del hijo ES el refrigerator_id
            }

            let json_raw = [obj_json_raw]

            const session = await fetchAuthSession()
            const authToken = session.tokens?.accessToken?.toString() || ''
            let dif_utc = -new Date().getTimezoneOffset() / 60

            const data = {
                authToken: authToken,
                body: {
                    form: JSON.stringify(json_raw),
                    process_now: true,
                    date: new Date().toISOString(),
                    schedule_id: schedule_id,
                    // checkin_id: validateStoreIdTypeIncidence ? store_id : (store_id && store_id != '') ? data_checkin_id : null,
                    dif_utc: dif_utc.toString(),
                },
            }

            console.log(
                '2 LE PEGO AL ENDPOINT QUE ENVIA EL FORMULARIO ===============> ',
            )

            const res = await request_save_form(data)

            executeLogicByFormType(formType, res)
        } catch (error) {
            console.error('sendFormNow Error ===>: ', error)
        }
    }

    const executeLogicByFormType = (formType, responseSaveForm) => {
        let status_send = responseSaveForm.status

        if (!status_send) {
            console.error('error al enviar form', form_id)
            return
        }

        if (formType === FORM_TYPES.FORM_ALIAS) {
            let data_alias = responseSaveForm?.response?.single?.data_alias
            status_send = responseSaveForm.status

            if (data_alias && data_alias.status === FORM_ALIAS_STATUS.ERROR) {
                console.error('error al enviar form', form_id, data_alias.message)
            }

            setbuttonActive(true)
            setMessageFormAlias(formType, data_alias)
            return
        }
    }

    const executeLoginByFormLostConnection = (formType) => {
        if (formType === FORM_TYPES.FORM_ALIAS) {
            const data_alias = {
                message: MESSAGES_FORM_ALIAS.ERROR,
                nickname: null,
                status: FORM_ALIAS_STATUS.ERROR,
            }

            setbuttonActive(true)
            setMessageFormAlias(formType, data_alias)
        }
    }

    const setMessageFormAlias = (formType, data_alias) => {
        if (formType === FORM_TYPES.FORM_ALIAS && data_alias != null) {
            // "message": "Alias confirmado \"Kenya Airways\"",
            //             "nickname": "Kenya Airways",
            //             "status": 1

            const message = data_alias?.message || null
            const nickname = data_alias?.nickname || null
            const status = data_alias?.status || null

            setMessageFormByResponseRequest(message)
            if (status === FORM_ALIAS_STATUS.SUCCESS) {
                deleteForm?.(nickname)
            } else {
                setbuttonActive(true)
            }
        }
    }

    const openFinishSheet = (onAccept?: () => void | Promise<void>) => {
        openBottomSheet(
            <FinishSheet
                closeBottomSheet={closeBottomSheet}
                title={title ?? ''}
                onAccept={onAccept}
            />,
        )
    }

    /**
     * @description agrego la key old_response en los contenidos depentiendo si tiene una respuesta guardada o no
     * @param formContents
     * @param responseGetFormsAnswers
     * @returns
     */
    const updateFormContent = (
        formContents: FormContentsModel[],
        responseGetFormsAnswers: ModelAnswerFromProps[],
    ) => {
        try {
            const newFormContent = formContents.map((contenido) => {
                if (responseGetFormsAnswers.length > 0) {
                    // console.log('-------->>>>> estoy if en el old response')
                    const findIndex = responseGetFormsAnswers.findIndex(
                        (answer) =>
                            answer._id.toString() === contenido._id.toString(),
                    )

                    if (findIndex !== -1) {
                        // lo encontre
                        return {
                            ...contenido,
                            old_response: responseGetFormsAnswers[findIndex],
                        }
                    } else {
                        if (contenido.old_response) {
                            const { old_response, ...contenidoSinOldResponse } =
                                contenido

                            return contenidoSinOldResponse
                        } else {
                            return contenido
                        }
                    }
                } else {
                    //console.log('-------->>>>> estoy else en el old response')

                    if (contenido.old_response) {
                        //console.log('🚀 ~ newFormContent ~ old_response:', contenido.old_response)

                        const { old_response, ...contenidoSinOldResponse } =
                            contenido

                        return contenidoSinOldResponse
                    } else {
                        return contenido
                    }
                }
            })

            return newFormContent
        } catch (error) {
            throw new Error('updateFormContent ERROR: ', error)
        }
    }
    const hideOrShowLocal = (
        hide?: string[],
        show?: string[],
        dataForm?: FormContentsModel[],
    ) => {
        //console.log('Debería ocultar estos', hide)
        // console.log(data.length, 'Debería mostrar estos', show)
        let dataMap = dataForm && dataForm.length > 0 ? dataForm : data

        const updatedFormContent = data.map((question) => {
            if (hide.length > 0 && hide.includes(question._id)) {
                return {
                    ...question,
                    hideQuestion: true,
                }
            }
            if (show.length > 0 && show.includes(question._id)) {
                return {
                    ...question,
                    hideQuestion: false,
                }
            }
            return question
        })

        setData(updatedFormContent)
        //setReloadHideShowData(!reloadHideShowData)
    }

    const filterByTag = (tag: string) => {
        // console.log('🚀 ~ filterByTag ~ tag:', tag)
        //*
        let updatedFormContent = []

        if (tag != '-1') {
            updatedFormContent = data.map((question) => {
                if (question.tag && question.tag._id != tag) {
                    return {
                        ...question,
                        hideByFilter: true,
                    }
                }
                return {
                    ...question,
                    hideByFilter: false,
                }
            })
        } else {
            updatedFormContent = data.map((question) => {
                return {
                    ...question,
                    hideByFilter: false,
                }
            })
        }
        setData(updatedFormContent)
    }

    const returnBarCode = async (code: string) => {
        try {
            console.log('Codigo leido', code)
            const foundItem = filteredData.findIndex(
                (item) => item.description_short === code,
            ) // Test
            // const foundItem = filteredData.findIndex((item) => item.code === code) //Real
            console.log('Item encontrado', foundItem)
            if (foundItem != -1) {
                setBarcodeActive(false)
                scrollToItem(foundItem)
            } else {
                setBarcodeActive(false)
                openBottomSheet(
                    <BarCodeErrorView
                        onPressRetry={() => {
                            closeBottomSheet(true)
                            canShowCamera()
                        }}
                    />,
                    ['45%'],
                )
            }
        } catch (error) {
            console.error('Error en returnBarCode:', error)
        }
    }

    const mainListRef = useRef(null)

    const [foundItemindex, setFoundItemIndex] = useState(-1)

    const scrollToItem = (index) => {
        closeBottomSheet(true)
        mainListRef.current?.scrollToIndex({ index: index, animated: true })
        setFoundItemIndex(index)
        setTimeout(() => setFoundItemIndex(-1), 1000)
    }

    // Dispara scrollToIndex marcándolo como programático para que los handlers
    // de scroll del usuario no lo interfieran ni lo reviertan.
    const scrollToFocusedIndex = (
        index: number,
        viewPositionOverride: number | null = null,
    ) => {
        const flatListH = flatListHeightRef.current
        const kbH = keyboardHeightRef.current
        const noKBH = flatListHeightNoKBRef.current

        // Detectar modo: si el FlatList encogió ≈ kbH → adjustResize; si el shrink < 90% del kbH → overlay
        const isAdjustResize = noKBH > 0 && kbH > 0 && noKBH - flatListH > kbH * 0.9

        const visibleH = isAdjustResize ? flatListH : Math.max(flatListH - kbH, 80)
        const ITEM_H = 70 // alto estimado del item de formulario

        // En adjustResize el FlatList encoge → flatListH ≈ visibleH, la fórmula clásica funciona.
        // En overlay el FlatList NO encoge → flatListH >> visibleH.
        //   La fórmula clásica (0.90 - ITEM_H/visibleH) da viewPosition demasiado alto:
        //   VP * flatListH > visibleH → item queda detrás del teclado.
        //   Fórmula correcta para overlay: VP = (visibleH - ITEM_H) / flatListH
        //   → garantiza VP * flatListH = visibleH - ITEM_H → item encaja justo arriba del teclado.
        const dynamicVP = isAdjustResize
            ? Math.max(0.2, Math.min(0.92, 0.9 - ITEM_H / visibleH))
            : Math.max(0, Math.min(0.5, (visibleH - ITEM_H) / flatListH))

        // viewPositionOverride: null → calcular dinámicamente (items top-level)
        //   0 → SiNoNaItem: viewPosition=0 siempre es seguro (top del padre visible)
        //   1 → ModuleShareItem: en overlay se capea al máximo seguro para no quedar detrás del teclado
        const maxSafeVP = isAdjustResize
            ? 0.92
            : Math.max(0, (visibleH - ITEM_H) / flatListH)
        const viewPosition =
            viewPositionOverride !== null
                ? Math.min(viewPositionOverride, maxSafeVP)
                : dynamicVP

        console.log(
            `[KBD] scrollToFocusedIndex → index=${index} | flatListH=${flatListH.toFixed(0)} kbH=${kbH.toFixed(0)} noKBH=${noKBH.toFixed(0)} | mode=${isAdjustResize ? 'adjustResize' : 'overlay'} viewPosition=${viewPosition.toFixed(3)}`,
        )
        isProgrammaticScrollRef.current = true
        mainListRef.current?.scrollToIndex({ index, animated: true, viewPosition })
        // Fallback: si no dispara onMomentumScrollEnd (scroll corto), resetear el flag
        setTimeout(() => {
            if (isProgrammaticScrollRef.current) {
                console.log('[KBD] isProgrammaticScroll reset por timeout')
                isProgrammaticScrollRef.current = false
            }
        }, 700)
    }

    const [barcode_active, setBarcodeActive] = useState(false)

    const canShowCamera = async () => {
        setBarcodeActive(true)
        // let title = languageFile.search_by_barcode
        // let desc = languageFile.scan_the_barcode

        // openBottomSheet(
        //     <>
        //         <CameraView
        //             closeBottomSheet={closeBottomSheet}
        //             returnedValue={(code: string) => returnBarCode(code)}
        //             title={title}
        //             description={desc}
        //             barcode={true}
        //         />
        //         <TouchableOpacity style={styles.paddingVertical} onPress={() => closeBottomSheet(true)}>
        //             <Text style={styles.floatingButtonText}>{languageFile.return_to_form}</Text>
        //             <View style={styles.horizontalLine}></View>
        //         </TouchableOpacity>
        //     </>,
        //     ['80%'],
        // )
        //returnBarCode('7791337007260') // => DASA
        // returnBarCode('6710017e37a5ecf8a4e887d6') // => id
    }

    const renderBarcodeCamera = () => {
        let title = languageFile.search_by_barcode
        let desc = languageFile.scan_the_barcode

        return (
            <Modal visible={barcode_active} animationType='slide'>
                <CameraView
                    closeBottomSheet={closeBottomSheet}
                    returnedValue={(code: string) => returnBarCode(code)}
                    title={title}
                    description={desc}
                    barcode={true}
                    closeCameraModal={() => setBarcodeActive(false)}
                />
                <TouchableOpacity
                    style={styles.paddingVertical}
                    onPress={() => setBarcodeActive(false)}
                >
                    <Text style={styles.floatingButtonText}>
                        {languageFile.return_to_form}
                    </Text>
                    <View style={styles.horizontalLine}></View>
                </TouchableOpacity>
            </Modal>
        )
        //returnBarCode('7791337007260') // => DASA
        // returnBarCode('6710017e37a5ecf8a4e887d6') // => id
    }

    const shouldShowButton = () => {
        const isNotFormImage = formType !== FORM_TYPES.FORM_IMAGE
        const isNotFormPopHeladera = formType !== FORM_TYPES.FORM_POP_HELADERA
        const isNotBlockedModerationForm = !(
            formType === FORM_TYPES.FORM_MODERACION && isUserBlocked()
        )
        const isNotPendingModeration = store_id
            ? getStoreStatus(store_id) !==
              CampaignStatus.STATUS_FORM_PENDING_MODERATION
            : true
        const isNotPendingCheckin =
            statusCheckin !== undefined && store_id
                ? statusCheckin !== CHECKIN_STATUS.CHECKIN_PENDIENTE
                : true
        const isNotStatusForm =
            typeof getStatusForm === 'function'
                ? getStatusForm() !== CampaignStatus.STATUS_FORM_PENDING_MODERATION
                : true

        return (
            isNotFormImage &&
            isNotFormPopHeladera &&
            isNotBlockedModerationForm &&
            isNotPendingModeration &&
            isNotPendingCheckin &&
            isNotStatusForm
        )
    }

    useEffect(() => {
        if (!isOpen && isFirstLoad) {
            initializeForm()
            setIsFirstLoad(false) // Para asegurarte de que solo se llama una vez
        }
    }, [isOpen, isFormRegistered]) // ✅ FIX: Agregar isFormRegistered como dependencia

    useEffect(() => {
        let hideTimeoutId: ReturnType<typeof setTimeout> | null = null

        const keyboardDidHideListener = Keyboard.addListener(
            'keyboardDidHide',
            () => {
                console.log('[KBD] keyboardDidHide')
                setKeyboardHidden(true)
                setKeyboardHeight(0)
                keyboardHeightRef.current = 0
                setKeyboardScrollPadding(0)
                pendingScrollIndexRef.current = null
                // Limpiar activeInputId después de que los children hayan procesado el cambio,
                // pero solo si no se enfocó otro input (protección para switch entre inputs)
                const currentId = activeInputIdRef.current
                hideTimeoutId = setTimeout(() => {
                    if (activeInputIdRef.current === currentId) {
                        setActiveInputId(null)
                        activeInputIdRef.current = null
                    }
                }, 150)
            },
        )
        const keyboardDidShowListener = Keyboard.addListener(
            'keyboardDidShow',
            (e) => {
                const kbHeight = e.endCoordinates.height
                console.log(
                    `[KBD] keyboardDidShow | altura=${kbHeight} | índice pendiente=${pendingScrollIndexRef.current}`,
                )
                setKeyboardHidden(false)
                setKeyboardHeight(kbHeight)
                keyboardHeightRef.current = kbHeight
                setKeyboardScrollPadding(kbHeight)
                // Cancelar el clear si el teclado se abrió de nuevo (switch entre inputs)
                if (hideTimeoutId) {
                    clearTimeout(hideTimeoutId)
                    hideTimeoutId = null
                }
                // Scroll al item pendiente ahora que el padding ya se aplicará en el próximo render.
                // Si pendingScrollIndexRef=null, keyboardDidShow llegó antes que onFocus (timing iOS):
                // handleInputFocus lo manejará desde la branch de teclado-abierto → no hacer nada aquí.
                const indexToScroll = pendingScrollIndexRef.current
                const viewPositionOverride = pendingScrollViewPositionRef.current
                const subInputMeasure = pendingSubInputScreenYRef.current
                pendingScrollIndexRef.current = null
                pendingScrollViewPositionRef.current = null
                pendingSubInputScreenYRef.current = null

                if (subInputMeasure) {
                    // Sub-input de ModuleShareItem: scroll preciso al input específico.
                    // Calculamos cuánto hay que scrollear para que el input quede JUSTO encima del teclado.
                    setTimeout(() => {
                        const screenH = Dimensions.get('window').height
                        const margin = Platform.OS === 'android' ? 60 : 12
                        const targetScreenY =
                            screenH - kbHeight - subInputMeasure.h - margin
                        const delta = subInputMeasure.y - targetScreenY
                        const newOffset = Math.max(
                            0,
                            currentScrollOffsetRef.current + delta,
                        )
                        console.log(
                            `[KBD] subInput scroll | screenY=${subInputMeasure.y.toFixed(0)} inputH=${subInputMeasure.h.toFixed(0)} targetY=${targetScreenY.toFixed(0)} delta=${delta.toFixed(0)} newOffset=${newOffset.toFixed(0)}`,
                        )
                        isProgrammaticScrollRef.current = true
                        mainListRef.current?.scrollToOffset({
                            offset: newOffset,
                            animated: true,
                        })
                    }, 50)
                } else if (indexToScroll !== null) {
                    console.log(
                        `[KBD] keyboardDidShow → scrolleando a index=${indexToScroll} (${pendingScrollIndexRef.current !== null ? 'pendiente' : 'último foco'})`,
                    )
                    // Pequeño delay para que FlatList re-renderice con el nuevo paddingBottom
                    setTimeout(
                        () =>
                            scrollToFocusedIndex(
                                indexToScroll,
                                viewPositionOverride,
                            ),
                        50,
                    )
                }
            },
        )
        return () => {
            keyboardDidHideListener.remove()
            keyboardDidShowListener.remove()
            if (hideTimeoutId) clearTimeout(hideTimeoutId)
        }
    }, [])

    // ✅ FIX: Actualizar data solo cuando formContent o answers REALMENTE cambien Y el formulario esté registrado
    useEffect(() => {
        // Permitir la primera carga sin restricción para mostrar el formulario vacío
        if (!isFormRegistered && formContent.length > 0) {
            const updatedData = updateFormContent(formContent, [])
            setData(updatedData)
            return
        }

        // Para actualizaciones posteriores, esperar a que el registro esté creado
        if (!isFormRegistered) return

        const formContentStr = JSON.stringify(formContent)
        const answersStr = JSON.stringify(answers)

        // Verificar si realmente cambió el contenido (no solo la referencia)
        if (
            formContentStr !== lastFormContentRef.current ||
            answersStr !== lastAnswersRef.current
        ) {
            lastFormContentRef.current = formContentStr
            lastAnswersRef.current = answersStr

            if (formContent.length > 0) {
                // ✅ FIX: Usar función callback para obtener el estado actual sin causar loops
                // Esto preserva el estado de visibilidad (hideQuestion) cuando se actualizan las respuestas
                setData((prevData) => {
                    const sourceData = prevData.length > 0 ? prevData : formContent
                    return updateFormContent(sourceData, answers)
                })
            }
        }
    }, [formContent, answers, isFormRegistered])

    // ✅ Validar cuando data cambie Y no esté vacío Y el formulario esté registrado
    useEffect(() => {
        if (data.length > 0 && isFormRegistered) {
            validatedComplet(data)
        }
    }, [data, isFormRegistered])

    // ✅ Actualizar progreso con debounce, solo si el formulario está registrado
    useEffect(() => {
        if (answers.length > 0 && isFormRegistered) {
            const timer = setTimeout(() => {
                checkStatusProgresForm()
            }, 500)
            return () => clearTimeout(timer)
        }
    }, [answers, isFormRegistered])

    useEffect(() => {
        setIsFirstLoad(true)
        setData(formContent)
        setTimeout(() => {
            setIsLoadingReload(false)
        }, 700)
    }, [reloadData])

    // Sincronizar data con formContent cuando se agregan nuevos items (ej: nueva heladera tipo 37)
    useEffect(() => {
        if (!formContent || formContent.length === 0) return
        if (!data || data.length === 0) return

        // Solo actuar si formContent tiene MÁS items que data (nuevos contenidos agregados)
        if (formContent.length > data.length) {
            const dataIds = new Set(data.map((d) => d._id))
            const hasNew = formContent.some((fc) => !dataIds.has(fc._id))
            if (hasNew) {
                console.log(
                    '🔄 [FormView] formContent tiene items nuevos, sincronizando data:',
                    {
                        dataCount: data.length,
                        formContentCount: formContent.length,
                    },
                )
                setData(formContent)
            }
        }
    }, [formContent])

    const showHeader = formHeader.subTitle != '' || formHeader.description != ''
    return (
        <>
            {isLoadingReload ? (
                <Loader />
            ) : (
                <View style={styles.container}>
                    {showHeader && (
                        <View
                            style={{
                                marginTop: 20,
                                marginHorizontal: 16,
                                paddingBottom: 20,
                                borderBottomColor: '#DEDFEE',
                                borderBottomWidth: 1,
                                marginBottom: 10,
                            }}
                        >
                            {formHeader.subTitle && (
                                <Text
                                    style={{
                                        fontSize: 16,
                                        fontWeight: '600',
                                        color: theme.colors.textPrimary,
                                    }}
                                >
                                    {formHeader.subTitle}
                                </Text>
                            )}
                            {formHeader.description && (
                                <Text
                                    style={{
                                        fontSize: 14,

                                        marginTop: 8,
                                        color: theme.colors.darkGray,
                                    }}
                                >
                                    {formHeader.description}
                                </Text>
                            )}
                        </View>
                    )}
                    <View style={styles.container}>
                        {show_barcode && (
                            <BarcodeButton canShowCamera={canShowCamera} />
                        )}

                        {tags && tags.length > 0 && (
                            <ScrollView
                                style={styles.scrollView2}
                                horizontal
                                showsHorizontalScrollIndicator={false}
                            >
                                <View style={styles.containerHorizontalTags}>
                                    <FormHorizontalTags
                                        tags={tags}
                                        data={data}
                                        onClickTag={filterByTag}
                                        fontWeight={'600'}
                                        lowerCase={true}
                                    />
                                    <View style={styles.spacer}></View>
                                </View>
                            </ScrollView>
                        )}

                        <View style={styles.spacerEight}></View>
                        <FlatList
                            ref={mainListRef}
                            data={filteredData} // data
                            removeClippedSubviews={false}
                            nestedScrollEnabled={true}
                            keyboardShouldPersistTaps='handled'
                            contentContainerStyle={{
                                paddingBottom: keyboardScrollPadding,
                            }}
                            onScroll={(e) => {
                                currentScrollOffsetRef.current =
                                    e.nativeEvent.contentOffset.y
                            }}
                            scrollEventThrottle={16}
                            onLayout={(e) => {
                                const h = e.nativeEvent.layout.height
                                flatListHeightRef.current = h
                                if (keyboardHeightRef.current === 0) {
                                    flatListHeightNoKBRef.current = h
                                }
                                console.log(
                                    `[KBD] FlatList layout | height=${h.toFixed(0)} | kb=${keyboardHeightRef.current > 0 ? 'abierto' : 'cerrado'} | noKB=${flatListHeightNoKBRef.current.toFixed(0)}`,
                                )
                            }}
                            onMomentumScrollEnd={() => {
                                isProgrammaticScrollRef.current = false
                            }}
                            keyExtractor={(item, index) =>
                                // Use a stable composite key to avoid collisions (item id + form id + index)
                                item && item._id
                                    ? `${form_id ?? ''}-${item._id}-${index}`
                                    : `${form_id ?? ''}-index-${index}`
                            }
                            horizontal={false}
                            showsVerticalScrollIndicator={false}
                            renderItem={({ item: item, index }) => (
                                <>
                                    {/* <Text>{item.type?.type}</Text> */}
                                    <FormContentItem
                                        item={item}
                                        // hideOrShow={hideOrShow ? hideOrShow : hideOrShowLocal}
                                        hideOrShow={hideOrShowLocal}
                                        form_id={form_id}
                                        schedule_id={schedule_id}
                                        store_id={store_id}
                                        checkComplete={checkComplete}
                                        data={data}
                                        name_form={name_form}
                                        checkin_id={checkin_id}
                                        keyboardHiddenState={keyboardHidden}
                                        onFocus={(id) => handleInputFocus(id)}
                                        activeInputId={activeInputId}
                                        onSubInputMeasure={handleSubInputMeasure}
                                        formType={formType}
                                        showError={incompleteQuestionIds.has(
                                            item._id,
                                        )}
                                        statusCheckin={statusCheckin}
                                        getStatusForm={getStatusForm}
                                        found={foundItemindex == index}
                                    />
                                </>
                            )}
                            numColumns={1}
                            onScrollToIndexFailed={(info) => {
                                setTimeout(() => {
                                    mainListRef.current?.scrollToIndex({
                                        index: info.index,
                                        animated: true,
                                        viewPosition: 0.3,
                                    })
                                }, 500)
                            }}
                            ItemSeparatorComponent={() => (
                                <View style={styles.dividerHorizontalLine} />
                            )}
                        />
                        <View style={styles.spacerZero}></View>

                        <View style={styles.spacerZero}></View>
                    </View>

                    {messageFormByResponseRequest && (
                        <View
                            style={{
                                flexDirection: 'row',
                                alignItems: 'center',
                                marginHorizontal: 15,
                                width: '85%',
                            }}
                        >
                            <Ionicons name='close-sharp' size={24} color='red' />

                            <Text
                                style={{
                                    marginStart: 6,
                                    color: 'red',
                                    fontWeight: '400',
                                    fontSize: 15,
                                }}
                            >
                                {messageFormByResponseRequest}
                            </Text>
                        </View>
                    )}

                    {/* Boton Enviar */}
                    {shouldShowButton() && (
                        <View
                            style={[
                                styles.buttonContainer,
                                {
                                    paddingVertical: 8,
                                },
                            ]}
                        >
                            <TouchableOpacity
                                onPress={() => handleSendFormNew()}
                                disabled={isSending}
                                style={[
                                    buttonActive && !isSending
                                        ? styles.mainButtonActive
                                        : styles.mainButton,
                                    {
                                        marginTop: 0,
                                    },
                                ]}
                            >
                                <Text
                                    style={[
                                        buttonActive
                                            ? styles.mainButtonTextActive
                                            : styles.mainButtonText,
                                    ]}
                                >
                                    {languageFile.send}
                                </Text>
                            </TouchableOpacity>
                        </View>
                    )}
                    {barcode_active && renderBarcodeCamera()}
                </View>
            )}
        </>
    )
}

const MemoizedFormView = React.memo(FormView)

export const BarcodeButton = ({ canShowCamera }: { canShowCamera: () => void }) => {
    const { languageFile } = getLanguage()

    return (
        <>
            <View style={styles.spacerEight}></View>
            <TouchableOpacity onPress={() => canShowCamera()}>
                <View style={styles.floatingButton}>
                    <Text style={styles.floatingButtonText}>
                        {languageFile.search_barcode}
                    </Text>
                    <Image
                        style={styles.floatingButtonIcon}
                        source={require('../../../assets/stores/codebar.png')}
                    />
                </View>
            </TouchableOpacity>
            <View style={styles.spacerEight}></View>
        </>
    )
}

export const BarCodeErrorView = ({ onPressRetry }: { onPressRetry: () => void }) => {
    const { languageFile } = getLanguage()

    return (
        <>
            <View style={styles.messageContainer}>
                <Image
                    style={styles.imageAsset}
                    source={require('../../../assets/barcodeNotFound.png')}
                />
                <Text style={styles.titleText}>
                    {languageFile.product_not_found}
                </Text>
                <Text style={styles.descriptionText}>
                    {languageFile.code_does_not_match}
                </Text>

                <View style={styles.buttonContainer}>
                    <TouchableOpacity
                        style={styles.borderButton}
                        onPress={onPressRetry}
                    >
                        <Text style={styles.borderButtonText}>
                            {languageFile.search_another_product}
                        </Text>
                    </TouchableOpacity>
                </View>
            </View>
        </>
    )
}

export const FinishSheet = ({
    closeBottomSheet,
    title,
    onAccept,
}: {
    closeBottomSheet: (l) => void
    title: string
    onAccept?: () => void | Promise<void>
}) => {
    const { languageFile } = getLanguage()

    const handleAccept = async () => {
        if (onAccept) {
            await onAccept()
        }
        closeBottomSheet(true)
    }

    return (
        <>
            <View style={styles.messageContainer}>
                <Image
                    style={styles.imageAsset}
                    source={require('../../../assets/wiseFormSent.png')}
                />

                <Text style={styles.titleText}>
                    {title
                        ? `¡${title}${languageFile.form_submitted_successfully}`
                        : ''}
                </Text>
                <Text style={styles.descriptionText}>
                    {languageFile.send_new_survey}
                </Text>

                <View style={styles.buttonContainer}>
                    <TouchableOpacity
                        style={styles.borderButton}
                        onPress={handleAccept}
                    >
                        <Text style={styles.borderButtonText}>
                            {languageFile.accept}
                        </Text>
                    </TouchableOpacity>
                </View>
            </View>
        </>
    )
}

interface FormContentItemProps {
    item: FormContentsModel
    form_id: string
    schedule_id: string
    store_id: string
    hideOrShow: (
        hides?: string[],
        show?: string[],
        data?: FormContentsModel[],
    ) => void
    checkComplete: (option?: ModelAnswerFromProps) => void
    data: FormContentsModel[]
    name_form: string
    checkin_id: string
    keyboardHiddenState: boolean
    onFocus: (id: string) => void
    activeInputId: string
    onSubInputMeasure?: (id: string, screenY: number, inputH: number) => void
    formType: number
    statusCheckin?: number | undefined
    getStatusForm?: () => number | null
    found?: boolean
    showError?: boolean
}

export const FormContentItem = React.memo(
    ({
        item,
        hideOrShow,
        form_id,
        schedule_id,
        store_id,
        checkComplete,
        data,
        name_form,
        checkin_id,
        keyboardHiddenState,
        onFocus,
        activeInputId,
        onSubInputMeasure,
        formType,
        statusCheckin,
        getStatusForm,
        found,
        showError,
    }: FormContentItemProps) => {
        // console.log('1647 --- FormContentItem => ', item.input_type?.type)
        return (
            <View style={{ opacity: found ? 0.5 : 1 }}>
                {/* <Text>{item.input_type?.type}</Text> */}
                {!item.hideQuestion && !item.hideByFilter ? (
                    <>
                        {(() => {
                            switch (item.input_type?.type) {
                                case 2:
                                    return <ImageFormItem item={item} />

                                case 4:
                                    return (
                                        <InputPhotoItem
                                            item={item}
                                            hasCamera={true}
                                            hasGalery={true}
                                            form_id={form_id}
                                            schedule_id={schedule_id}
                                            store_id={store_id}
                                            checkComplete={checkComplete}
                                        />
                                    )

                                case 5:
                                    return (
                                        <InputCommentItem
                                            item={item}
                                            form_id={form_id}
                                            schedule_id={schedule_id}
                                            store_id={store_id}
                                            checkComplete={checkComplete}
                                            keyboardHiddenState={keyboardHiddenState}
                                            onFocus={onFocus}
                                            activeInputId={activeInputId}
                                        />
                                    )

                                case 7:
                                case 8:
                                case 14:
                                case 18:
                                    return (
                                        <SiNoNaItem
                                            item={item}
                                            hideOrShow={hideOrShow}
                                            form_id={form_id}
                                            schedule_id={schedule_id}
                                            store_id={store_id}
                                            checkComplete={checkComplete}
                                            data={data}
                                            name_form={name_form}
                                            keyboardHiddenState={keyboardHiddenState}
                                            onFocus={onFocus}
                                            activeInputId={activeInputId}
                                            onSubInputMeasure={onSubInputMeasure}
                                        />
                                    )

                                case 9:
                                    return (
                                        <InputNumberItem
                                            item={item}
                                            form_id={form_id}
                                            schedule_id={schedule_id}
                                            store_id={store_id}
                                            checkComplete={checkComplete}
                                            name_form={name_form}
                                            keyboardHiddenState={keyboardHiddenState}
                                            onFocus={onFocus}
                                            activeInputId={activeInputId}
                                        />
                                    )

                                case 10:
                                case 36:
                                    return (
                                        <DropdownItem
                                            item={item}
                                            hideOrShow={hideOrShow}
                                            form_id={form_id}
                                            schedule_id={schedule_id}
                                            store_id={store_id}
                                            checkComplete={checkComplete}
                                            data={data}
                                        />
                                    ) //Dropdown
                                case 11:
                                    return (
                                        <DropdownMultipleSelectItem
                                            item={item}
                                            hideOrShow={hideOrShow}
                                            form_id={form_id}
                                            schedule_id={schedule_id}
                                            store_id={store_id}
                                            checkComplete={checkComplete}
                                        />
                                    ) //DropdownMultipleSelect

                                case 16:
                                    return (
                                        <InputButtonItem
                                            item={item}
                                            form_id={form_id}
                                            schedule_id={schedule_id}
                                            store_id={store_id}
                                            checkComplete={checkComplete}
                                        />
                                    ) // Heladera

                                case 21:
                                    return (
                                        <InputPriceNa
                                            item={item}
                                            name_form={name_form}
                                            form_id={form_id}
                                            schedule_id={schedule_id}
                                            store_id={store_id}
                                            checkComplete={checkComplete}
                                            keyboardHiddenState={keyboardHiddenState}
                                            onFocus={onFocus}
                                            activeInputId={activeInputId}
                                        />
                                    )

                                case 32: // foto sin galeria
                                    return (
                                        <InputPhotoItem
                                            item={item}
                                            hasCamera={true}
                                            hasGalery={false}
                                            form_id={form_id}
                                            schedule_id={schedule_id}
                                            store_id={store_id}
                                            checkComplete={checkComplete}
                                        />
                                    )

                                case 33: // foto sin camara
                                    return (
                                        <InputPhotoItem
                                            item={item}
                                            hasCamera={false}
                                            hasGalery={true}
                                            form_id={form_id}
                                            schedule_id={schedule_id}
                                            store_id={store_id}
                                            checkComplete={checkComplete}
                                        />
                                    )

                                case 37: // input_forms
                                    return (
                                        <InputFormItem
                                            item={item}
                                            form_id={form_id}
                                            schedule_id={schedule_id}
                                            store_id={store_id}
                                            checkComplete={checkComplete}
                                            checkin_id={checkin_id}
                                        />
                                    )

                                case 38:
                                    return (
                                        <InputCommentNoEditItem
                                            item={item}
                                            form_id={form_id}
                                            schedule_id={schedule_id}
                                            store_id={store_id}
                                            checkComplete={checkComplete}
                                        />
                                    )

                                case 41:
                                    return (
                                        <InputPhotoImageItem
                                            item={item}
                                            hasCamera={true}
                                            hasGalery={true}
                                            form_id={form_id}
                                            schedule_id={schedule_id}
                                            store_id={store_id}
                                            checkComplete={checkComplete}
                                            formType={formType}
                                            statusCheckin={statusCheckin}
                                            getStatusForm={getStatusForm}
                                        />
                                    )

                                case 51: // fotos múltiples (límite en item.label)
                                    return (
                                        <InputPhotoMultipleItem
                                            item={item}
                                            form_id={form_id}
                                            schedule_id={schedule_id}
                                            store_id={store_id}
                                            checkComplete={checkComplete}
                                        />
                                    )

                                case 47:
                                    return (
                                        <ModuleShareItem
                                            item={item}
                                            hideOrShow={hideOrShow}
                                            form_id={form_id}
                                            schedule_id={schedule_id}
                                            store_id={store_id}
                                            checkComplete={checkComplete}
                                            data={data}
                                            name_form={name_form}
                                            keyboardHiddenState={keyboardHiddenState}
                                            onFocus={onFocus}
                                            activeInputId={activeInputId}
                                            onSubInputMeasure={onSubInputMeasure}
                                        />
                                    )

                                default:
                                    return <Text>{item.title} item not found</Text>
                            }
                        })()}
                        {showError && (
                            <Text
                                style={{
                                    color: theme.colors.colorDisapproved,
                                    fontSize: 12,
                                    fontWeight: '600',
                                    marginHorizontal: 15,
                                    marginBottom: 8,
                                    marginTop: -1,
                                }}
                            >
                                * Este campo es requerido
                            </Text>
                        )}
                    </>
                ) : (
                    <></>
                )}
            </View>
        )
    },
)
